import { HandoffView } from "./HandoffView";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent
} from "@dnd-kit/core";
import {
  Archive,
  ArrowRight,
  BarChart3,
  BookOpen,
  Boxes,
  CalendarDays,
  Check,
  ChevronRight,
  CircleDollarSign,
  CircleHelp,
  Copy,
  Download,
  ExternalLink,
  FileAudio,
  FileText,
  Flame,
  GripVertical,
  Image,
  KanbanSquare,
  LayoutDashboard,
  Lightbulb,
  Link2,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Maximize2,
  Menu,
  Minimize2,
  Moon,
  MoreHorizontal,
  ListChecks,
  ListTodo,
  Pause,
  PanelLeftClose,
  PanelLeftOpen,
  Paperclip,
  Pencil,
  Play,
  Plus,
  Rocket,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Sun,
  Square,
  Target,
  Timer,
  Trash2,
  Upload,
  UserCog,
  UserPlus,
  Users,
  Video as VideoIcon,
  X
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type FormEvent,
  type ReactNode
} from "react";
import { api, formatBytes, formValues } from "./api";
import type {
  Asset,
  AuthStatus,
  BootstrapData,
  Channel,
  ChannelReference,
  ChannelTopicSource,
  DailyTask,
  DeepWorkSession,
  HeyGenAccount,
  IdeationPattern,
  Niche,
  NicheObsidianNote,
  NicheReference,
  Product,
  User,
  Video,
  ViewName
} from "./types";

const OBSIDIAN_VAULT_NAME =
  import.meta.env.VITE_OBSIDIAN_VAULT_NAME || "vault";
const CAMPAIGN_START_DATE =
  import.meta.env.VITE_CAMPAIGN_START_DATE || "";

const VIDEO_COLUMNS = [
  { id: "new", label: "New", color: "#8b93a7", hint: "Topic mới" },
  { id: "script", label: "Scripting", color: "#fb7185", hint: "Script + QA" },
  { id: "voiceover", label: "Voiceover", color: "#f97316", hint: "Render / attach finished VO" },
  { id: "ready", label: "Ready", color: "#f59e0b", hint: "Ready to generate" },
  { id: "generation", label: "In generation", color: "#fbbf24", hint: "Visual generation + assembly" },
  { id: "qa", label: "Generated / QA", color: "#34d399", hint: "Review output" },
  { id: "package", label: "Package", color: "#14b8a6", hint: "Thumbnail + title + description" },
  { id: "scheduled", label: "Scheduled", color: "#a78bfa", hint: "Publish queue" },
  { id: "posted", label: "Posted", color: "#c084fc", hint: "Live" },
  { id: "archived", label: "Archive", color: "#64748b", hint: "Closed" }
] as const;

type ProductionFormatId = "avatarforge" | "vidforge" | "sketch" | "hybrid" | "other";

type PendingAsset = {
  id: string;
  file: File;
  type: string;
};

const ASSET_TYPE_OPTIONS = [
  ["script", "Script file"],
  ["voiceover", "Voiceover"],
  ["thumbnail", "Thumbnail"],
  ["render", "Video render"],
  ["source", "Source / research"],
  ["other", "Other"]
] as const;

const PRODUCTION_FORMATS: Record<ProductionFormatId, {
  label: string;
  shortLabel: string;
  color: string;
}> = {
  avatarforge: { label: "AvatarForge", shortLabel: "AVATAR", color: "#f59e0b" },
  vidforge: { label: "VidForge", shortLabel: "VIDFORGE", color: "#38bdf8" },
  sketch: { label: "Sketch Explainer", shortLabel: "SKETCH", color: "#a78bfa" },
  hybrid: { label: "Hybrid", shortLabel: "HYBRID", color: "#fb7185" },
  other: { label: "Other", shortLabel: "OTHER", color: "#8b93a7" }
};

const PRODUCTION_STACK_OPTIONS = [
  { id: "avatarforge", label: "AvatarForge" },
  { id: "vidforge", label: "VidForge" },
  { id: "sketch", label: "Sketch Explainer" },
  { id: "vidcrush", label: "VidCrush" },
  { id: "heygen", label: "HeyGen" },
  { id: "remotion", label: "Remotion" },
  { id: "other", label: "Other" }
] as const;

const WORKFLOW_TEMPLATES: Record<ProductionFormatId, { id: string; label: string }[]> = {
  avatarforge: [
    { id: "script", label: "Script locked" },
    { id: "voiceover", label: "Voiceover finished" },
    { id: "avatar_render", label: "Avatar render finished" },
    { id: "qa", label: "Video QA passed" },
    { id: "package", label: "Title, thumbnail & description ready" }
  ],
  vidforge: [
    { id: "script", label: "Script locked" },
    { id: "shot_plan", label: "Shot / visual plan ready" },
    { id: "voiceover", label: "Voiceover finished" },
    { id: "visual_generation", label: "VidForge visuals generated" },
    { id: "assembly", label: "Assembly / render finished" },
    { id: "qa", label: "Video QA passed" },
    { id: "package", label: "Title, thumbnail & description ready" }
  ],
  sketch: [
    { id: "script", label: "Script locked" },
    { id: "scene_breakdown", label: "Scene breakdown ready" },
    { id: "voiceover", label: "Voiceover finished" },
    { id: "sketch_render", label: "Sketch scenes rendered" },
    { id: "assembly", label: "Assembly / render finished" },
    { id: "qa", label: "Video QA passed" },
    { id: "package", label: "Title, thumbnail & description ready" }
  ],
  hybrid: [
    { id: "script", label: "Script locked" },
    { id: "visual_plan", label: "Hybrid visual plan ready" },
    { id: "voiceover", label: "Voiceover finished" },
    { id: "primary_generation", label: "Primary format generated" },
    { id: "secondary_generation", label: "Secondary format generated" },
    { id: "assembly", label: "Assembly / render finished" },
    { id: "qa", label: "Video QA passed" },
    { id: "package", label: "Title, thumbnail & description ready" }
  ],
  other: [
    { id: "script", label: "Script locked" },
    { id: "voiceover", label: "Voiceover finished" },
    { id: "generation", label: "Visual generation finished" },
    { id: "qa", label: "Video QA passed" },
    { id: "package", label: "Title, thumbnail & description ready" }
  ]
};

function productionFormat(value?: string | null): ProductionFormatId {
  return Object.prototype.hasOwnProperty.call(PRODUCTION_FORMATS, value || "")
    ? value as ProductionFormatId
    : "other";
}

function parseStringList(value?: string | null, fallback: string[] = []) {
  try {
    const parsed = JSON.parse(String(value || ""));
    return Array.isArray(parsed)
      ? [...new Set(parsed.map((item) => String(item).trim()).filter(Boolean))]
      : fallback;
  } catch {
    return fallback;
  }
}

function nicheFormats(niche: Niche): ProductionFormatId[] {
  const parsed = parseStringList(niche.planned_formats);
  if (parsed.length) return parsed.map(productionFormat);
  const raw = String(niche.format || "").toLowerCase();
  const legacy = [
    raw.includes("avatar") ? "avatarforge" : "",
    raw.includes("vidforge") ? "vidforge" : "",
    raw.includes("sketch") ? "sketch" : ""
  ].filter(Boolean) as ProductionFormatId[];
  return legacy.length ? legacy : ["other"];
}

function effectiveVideoFormat(video: Video, channel?: Channel) {
  return productionFormat(video.format_override || channel?.primary_format || "avatarforge");
}

function parseChecklist(value?: string | null): Record<string, boolean> {
  try {
    const parsed = JSON.parse(String(value || "{}"));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? Object.fromEntries(Object.entries(parsed).map(([key, checked]) => [key, Boolean(checked)]))
      : {};
  } catch {
    return {};
  }
}

const PRODUCT_COLUMNS = [
  { id: "idea", label: "Idea", color: "#8b93a7" },
  { id: "offer", label: "Offer design", color: "#38bdf8" },
  { id: "building", label: "Building", color: "#f59e0b" },
  { id: "review", label: "Review", color: "#34d399" },
  { id: "live", label: "Live", color: "#c084fc" },
  { id: "scaling", label: "Scaling", color: "#fb7185" }
] as const;

const NICHE_STATUS: Record<string, { label: string; color: string }> = {
  idea: { label: "Idea", color: "#8b93a7" },
  research: { label: "Research", color: "#38bdf8" },
  validation: { label: "Validation", color: "#f59e0b" },
  launch_ready: { label: "Launch queue", color: "#34d399" },
  running: { label: "Running", color: "#c084fc" },
  parked: { label: "Parked", color: "#64748b" }
};

const PATTERN_STATUS: Record<string, { label: string; color: string }> = {
  captured: { label: "Captured", color: "#8b93a7" },
  bending: { label: "Bending", color: "#38bdf8" },
  test_queue: { label: "Test queue", color: "#f59e0b" },
  testing: { label: "Testing", color: "#c084fc" },
  winner: { label: "Winner", color: "#34d399" },
  fatigued: { label: "Fatigued", color: "#fb7185" },
  archived: { label: "Archived", color: "#64748b" }
};

const CHANNEL_2FA_STATUS = {
  not_checked: { label: "2FA chưa check" },
  recovery_90d: { label: "90d recovery attached" },
  owner_only: { label: "Owner 2FA only" }
} as const;

const HEYGEN_ACCOUNT_STATUS = {
  login_success: { label: "Log thành công" },
  api_connected: { label: "Connected API" },
  failed: { label: "Fail" }
} as const;

const READINESS_FIELDS: (keyof Channel)[] = [
  "name",
  "niche",
  "description",
  "reference_url",
  "youtube_url",
  "profile_image_url",
  "banner_url",
  "heygen_avatar_id",
  "voice_id",
  "thumbnail_prompt",
  "title_engine"
];

function readiness(channel: Channel) {
  const filled = READINESS_FIELDS.filter((field) =>
    String(channel[field] || "").trim()
  ).length;
  return Math.round((filled / READINESS_FIELDS.length) * 100);
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function formatDate(date?: string | null) {
  if (!date) return "Chưa đặt";
  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit"
  }).format(new Date(`${date}T12:00:00`));
}

function localDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function localWeekStartKey(date = new Date()) {
  const start = new Date(date);
  const daysSinceMonday = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - daysSinceMonday);
  return localDateKey(start);
}

function sessionSeconds(session: DeepWorkSession, nowMs = Date.now()) {
  const liveSeconds =
    session.status === "running" && session.running_since
      ? Math.max(
          0,
          Math.floor((nowMs - new Date(session.running_since).getTime()) / 1000)
        )
      : 0;
  return session.accumulated_seconds + liveSeconds;
}

function formatTimer(seconds: number) {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remaining = safe % 60;
  return [hours, minutes, remaining]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
}

function formatWorkTotal(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

function safeExternalUrl(url?: string | null) {
  const value = String(url || "").trim();
  return /^https?:\/\//i.test(value) ? value : "";
}

function obsidianNoteUrl(note?: string | null) {
  let value = String(note || "").trim();
  if (!value) return "";
  if (/^obsidian:\/\//i.test(value)) return value;
  if (value.startsWith("[[") && value.endsWith("]]")) {
    value = value.slice(2, -2).split("|")[0].trim();
  }

  const looksLikeNote = value.includes("/") || /\.md(?:#|$)/i.test(value);
  if (!looksLikeNote) return "";
  if (value.startsWith("/")) {
    return `obsidian://open?path=${encodeURIComponent(value)}`;
  }

  const relativePath = value.replace(/^\/+/, "");
  return `obsidian://open?vault=${encodeURIComponent(OBSIDIAN_VAULT_NAME)}&file=${encodeURIComponent(relativePath)}`;
}

function obsidianNoteLabel(note?: string | null, index = 1) {
  let value = String(note || "").trim();
  if (!value) return `Research note ${index}`;
  try {
    if (/^obsidian:\/\//i.test(value)) {
      const uri = new URL(value);
      value = uri.searchParams.get("file") || uri.searchParams.get("path") || value;
    }
  } catch {
    // Keep the original value and derive the best available label below.
  }
  if (value.startsWith("[[") && value.endsWith("]]")) {
    value = value.slice(2, -2).split("|")[0].trim();
  }
  const withoutHeading = value.split("#")[0].replace(/\.md$/i, "");
  const label = withoutHeading.split("/").filter(Boolean).pop()?.trim();
  return label || `Research note ${index}`;
}

const VALID_VIEWS: ViewName[] = ["handoff",
  "dashboard",
  "production",
  "channels",
  "niches",
  "products",
  "heygen",
  "users"
];

function initialWorkspaceView(): ViewName {
  const hashView = window.location.hash.replace(/^#\/?/, "");
  if (VALID_VIEWS.includes(hashView as ViewName)) return hashView as ViewName;
  const savedView = window.localStorage.getItem("forge-current-view");
  return VALID_VIEWS.includes(savedView as ViewName)
    ? savedView as ViewName
    : "dashboard";
}

function App() {
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [data, setData] = useState<BootstrapData | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [heyGenAccounts, setHeyGenAccounts] = useState<HeyGenAccount[]>([]);
  const [view, setView] = useState<ViewName>(initialWorkspaceView);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => window.localStorage.getItem("forge-sidebar-collapsed") === "true"
  );
  const [channelFilter, setChannelFilter] = useState("all");
  const [productionFormatFilter, setProductionFormatFilter] = useState("all");
  const [channelFormatFilter, setChannelFormatFilter] = useState("all");
  const [channelStatusFilter, setChannelStatusFilter] = useState("all");
  const [nicheFormatFilter, setNicheFormatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [channelSearch, setChannelSearch] = useState("");
  const [nicheSearch, setNicheSearch] = useState("");
  const [videoEdit, setVideoEdit] = useState<Partial<Video> | null>(null);
  const [channelEdit, setChannelEdit] = useState<Partial<Channel> | null>(null);
  const [nicheEdit, setNicheEdit] = useState<Partial<Niche> | null>(null);
  const [patternEdit, setPatternEdit] = useState<Partial<IdeationPattern> | null>(null);
  const [nicheLabMode, setNicheLabMode] = useState<"niches" | "patterns">("niches");
  const [productEdit, setProductEdit] = useState<Partial<Product> | null>(null);
  const [userEdit, setUserEdit] = useState<(Partial<User> & { password?: string }) | null>(null);
  const [heyGenEdit, setHeyGenEdit] = useState<
    (Partial<HeyGenAccount> & { password?: string }) | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    const saved = window.localStorage.getItem("forge-theme");
    if (saved === "dark" || saved === "light") return saved;
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem("forge-theme", theme);
  }, [theme]);

  useEffect(() => {
    window.localStorage.setItem(
      "forge-sidebar-collapsed",
      String(sidebarCollapsed)
    );
  }, [sidebarCollapsed]);

  useEffect(() => {
    window.localStorage.setItem("forge-current-view", view);
    const nextHash = `#${view}`;
    if (window.location.hash !== nextHash) {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}${nextHash}`
      );
    }
  }, [view]);

  useEffect(() => {
    const syncViewFromHash = () => {
      const hashView = window.location.hash.replace(/^#\/?/, "");
      if (VALID_VIEWS.includes(hashView as ViewName)) {
        setView(hashView as ViewName);
      }
    };
    window.addEventListener("hashchange", syncViewFromHash);
    return () => window.removeEventListener("hashchange", syncViewFromHash);
  }, []);

  async function load(currentUser = auth?.user, rolloverTasks = true) {
    if (rolloverTasks) {
      await api("/api/daily-tasks/rollover", {
        method: "POST",
        body: JSON.stringify({ work_date: localDateKey() })
      });
    }
    const bootstrap = await api<BootstrapData>("/api/bootstrap");
    setData(bootstrap);
    if (currentUser?.role === "admin") {
      const [nextUsers, nextHeyGenAccounts] = await Promise.all([
        api<User[]>("/api/users"),
        api<HeyGenAccount[]>("/api/heygen-accounts")
      ]);
      setUsers(nextUsers);
      setHeyGenAccounts(nextHeyGenAccounts);
    } else {
      setUsers([]);
      setHeyGenAccounts([]);
    }
  }

  useEffect(() => {
    api<AuthStatus>("/api/auth/status")
      .then((status) => {
        setAuth(status);
        if (status.authenticated) return load(status.user);
      })
      .catch((error) => setNotice(error.message));
  }, []);

  useEffect(() => {
    if (
      auth?.authenticated &&
      auth.user?.role !== "admin" &&
      (view === "heygen" || view === "users")
    ) {
      setView("dashboard");
    }
  }, [auth?.authenticated, auth?.user?.role, view]);

  useEffect(() => {
    if (!auth?.authenticated || !auth.user) return;

    const source = new EventSource("/api/events");
    let refreshTimer: number | undefined;
    let refreshing = false;
    let refreshQueued = false;
    let disposed = false;

    const refreshSharedData = async () => {
      if (refreshing) {
        refreshQueued = true;
        return;
      }
      refreshing = true;
      try {
        const bootstrap = await api<BootstrapData>("/api/bootstrap");
        if (disposed) return;
        setData(bootstrap);
        if (auth.user?.role === "admin") {
          const [nextUsers, nextHeyGenAccounts] = await Promise.all([
            api<User[]>("/api/users"),
            api<HeyGenAccount[]>("/api/heygen-accounts")
          ]);
          if (!disposed) {
            setUsers(nextUsers);
            setHeyGenAccounts(nextHeyGenAccounts);
          }
        }
      } catch {
        // EventSource reconnects automatically; keep the current optimistic UI.
      } finally {
        refreshing = false;
        if (refreshQueued && !disposed) {
          refreshQueued = false;
          void refreshSharedData();
        }
      }
    };

    const scheduleRefresh = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => void refreshSharedData(), 120);
    };

    source.addEventListener("sync", scheduleRefresh);
    return () => {
      disposed = true;
      window.clearTimeout(refreshTimer);
      source.removeEventListener("sync", scheduleRefresh);
      source.close();
    };
  }, [auth?.authenticated, auth?.user?.id, auth?.user?.role]);

  async function login(username: string, password: string) {
    setBusy(true);
    try {
      const result = await api<{ user: User }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password })
      });
      const nextAuth: AuthStatus = {
        required: true,
        authenticated: true,
        setup_required: false,
        user: result.user
      };
      setAuth(nextAuth);
      await load(result.user);
    } finally {
      setBusy(false);
    }
  }

  async function setupAdmin(payload: {
    username: string;
    display_name: string;
    password: string;
  }) {
    setBusy(true);
    try {
      const result = await api<{ user: User }>("/api/auth/setup", {
        method: "POST",
        body: JSON.stringify(payload)
      });
      const nextAuth: AuthStatus = {
        required: true,
        authenticated: true,
        setup_required: false,
        user: result.user
      };
      setAuth(nextAuth);
      await load(result.user);
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    setAuth({
      required: true,
      authenticated: false,
      setup_required: false,
      user: null
    });
    setData(null);
    setUsers([]);
    setHeyGenAccounts([]);
    setView("dashboard");
  }

  function flash(message: string) {
    setNotice(message);
    window.setTimeout(() => {
      setNotice((current) => current === message ? null : current);
    }, 2200);
  }

  async function commitOptimistic<T>(
    action: () => Promise<T>,
    onCommit: (result: T) => void,
    onRollback: () => void,
    success = "Đã lưu thay đổi"
  ) {
    try {
      const result = await action();
      onCommit(result);
      flash(success);
      return result;
    } catch (error) {
      onRollback();
      setNotice(error instanceof Error ? error.message : "Có lỗi xảy ra");
      throw error;
    }
  }

  if (!auth) return <LoadingScreen />;
  if (!auth.authenticated) {
    return (
      <LoginScreen
        setupRequired={auth.setup_required}
        onLogin={login}
        onSetup={setupAdmin}
        busy={busy}
      />
    );
  }
  if (!data) return <LoadingScreen />;

  const addForView = () => {
    if (view === "production") {
      setVideoEdit({ channel_id: channelFilter === "all" ? data.channels[0]?.id : channelFilter });
    } else if (view === "channels") {
      setChannelEdit({
        launch_date: "",
        color: "#f59e0b",
        primary_format: "avatarforge",
        production_stack: '["avatarforge"]',
        workflow_template: "avatarforge"
      });
    } else if (view === "niches") {
      if (nicheLabMode === "patterns") {
        setPatternEdit({ status: "captured" });
      } else {
        setNicheEdit({
          market: "US",
          format: "AvatarForge",
          planned_formats: '["avatarforge"]',
          status: "idea",
          conviction: 50
        });
      }
    } else if (view === "products") {
      setProductEdit({ type: "ebook", status: "idea", currency: "USD" });
    } else if (view === "heygen") {
      setHeyGenEdit({ status: "login_success" });
    } else if (view === "users") {
      setUserEdit({ role: "member", status: "active" });
    } else {
      setVideoEdit({ channel_id: data.channels[0]?.id });
    }
  };

  const pageMeta: Record<ViewName, { eyebrow: string; title: string; action: string }> = {
    handoff: { eyebrow: "DSH writers → production", title: "Thư viện bàn giao", action: "" },
    dashboard: {
      eyebrow: "Media Studio Operations",
      title: "Command Center",
      action: "Add video"
    },
    production: {
      eyebrow: "Idea → publish",
      title: "Production Board",
      action: "Add video"
    },
    channels: {
      eyebrow: "Channel network",
      title: "Channel HQ",
      action: "Add channel"
    },
    niches: {
      eyebrow: "Research parking lot",
      title: "Niche Lab",
      action: "Add niche"
    },
    products: {
      eyebrow: "Audience → owned revenue",
      title: "Monetization Lab",
      action: "Add product"
    },
    heygen: {
      eyebrow: "Disposable account list",
      title: "HeyGen Account Manager",
      action: "Add account"
    },
    users: {
      eyebrow: "Admin controls",
      title: "User Management",
      action: "Add user"
    }
  };
  const searchableView = ["production", "channels", "niches", "heygen"].includes(view);
  const activeSearch =
    view === "channels" ? channelSearch : view === "niches" ? nicheSearch : search;
  const searchPlaceholder =
    view === "channels"
      ? "Search channels"
      : view === "niches"
        ? nicheLabMode === "patterns" ? "Search patterns" : "Search niches"
        : view === "heygen"
          ? "Search HeyGen accounts"
        : "Search videos";
  const updateActiveSearch = (value: string) => {
    if (view === "channels") setChannelSearch(value);
    else if (view === "niches") setNicheSearch(value);
    else setSearch(value);
  };

  return (
    <div className={`app-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <Sidebar
        view={view}
        setView={(next) => {
          setView(next);
          setSidebarOpen(false);
        }}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed((current) => !current)}
        data={data}
        currentUser={auth.user}
        logout={logout}
      />

      <main className="app-main">
        <header className="topbar">
          <button
            className="icon-button mobile-menu"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
          >
            <Menu size={19} />
          </button>
          <div>
            <p className="eyebrow">{pageMeta[view].eyebrow}</p>
            <h1>{pageMeta[view].title}</h1>
          </div>
          <div className="topbar-actions">
            <button
              className="icon-button theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {searchableView && (
              <label className="search-box">
                <Search size={16} />
                <input
                  value={activeSearch}
                  onChange={(event) => updateActiveSearch(event.target.value)}
                  placeholder={searchPlaceholder}
                />
              </label>
            )}
            {view === "production" && (
              <>
                <select
                  className="select-control format-filter"
                  value={productionFormatFilter}
                  onChange={(event) => setProductionFormatFilter(event.target.value)}
                  aria-label="Filter by production format"
                >
                  <option value="all">All formats</option>
                  {Object.entries(PRODUCTION_FORMATS).map(([id, item]) => (
                    <option key={id} value={id}>{item.label}</option>
                  ))}
                </select>
                <select
                  className="select-control"
                  value={channelFilter}
                  onChange={(event) => setChannelFilter(event.target.value)}
                  aria-label="Filter by channel"
                >
                  <option value="all">All channels</option>
                  {data.channels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name}
                    </option>
                  ))}
                </select>
              </>
            )}
            {view !== "handoff" && <button className="primary-button" onClick={addForView}>
              <Plus size={16} />
              <span>{view === "niches" && nicheLabMode === "patterns" ? "Add pattern" : pageMeta[view].action}</span>
            </button>}
          </div>
        </header>

        <section className="page-content">
          {view === "handoff" && auth.user && <HandoffView user={auth.user} />}
          {view === "dashboard" && (
            <DashboardView
              data={data}
              onOpenChannel={setChannelEdit}
              onGoProduction={() => setView("production")}
              onAddVideo={() => setVideoEdit({ channel_id: data.channels[0]?.id })}
              onStartDeepWork={() => {
                const timestamp = new Date().toISOString();
                const temp: DeepWorkSession = {
                  id: `temp-${crypto.randomUUID()}`,
                  user_id: auth.user?.id || "",
                  work_date: localDateKey(),
                  status: "running",
                  accumulated_seconds: 0,
                  running_since: timestamp,
                  started_at: timestamp,
                  ended_at: null,
                  created_at: timestamp,
                  updated_at: timestamp
                };
                setData((current) => current ? {
                  ...current,
                  deep_work_sessions: [temp, ...current.deep_work_sessions]
                } : current);
                return commitOptimistic(
                  () => api<DeepWorkSession>("/api/deep-work/start", {
                    method: "POST",
                    body: JSON.stringify({ work_date: localDateKey() })
                  }),
                  (saved) => setData((current) => current ? {
                    ...current,
                    deep_work_sessions: current.deep_work_sessions.map((session) => session.id === temp.id ? saved : session)
                  } : current),
                  () => setData((current) => current ? {
                    ...current,
                    deep_work_sessions: current.deep_work_sessions.filter((session) => session.id !== temp.id)
                  } : current),
                  "Deep Work đã bắt đầu"
                );
              }}
              onDeepWorkAction={(session, action) => {
                const timestamp = new Date().toISOString();
                const optimisticSession: DeepWorkSession = {
                  ...session,
                  status: action === "pause" ? "paused" : action === "resume" ? "running" : "completed",
                  accumulated_seconds: action === "pause" || action === "stop"
                    ? sessionSeconds(session)
                    : session.accumulated_seconds,
                  running_since: action === "resume" ? timestamp : action === "pause" || action === "stop" ? null : session.running_since,
                  ended_at: action === "stop" ? timestamp : session.ended_at,
                  updated_at: timestamp
                };
                setData((current) => current ? {
                  ...current,
                  deep_work_sessions: current.deep_work_sessions.map((item) =>
                    item.id === session.id ? optimisticSession : item
                  )
                } : current);
                return commitOptimistic(
                  () => api<DeepWorkSession>(`/api/deep-work/${session.id}/${action}`, { method: "POST" }),
                  (saved) => setData((current) => current ? {
                    ...current,
                    deep_work_sessions: current.deep_work_sessions.map((item) => item.id === saved.id ? saved : item)
                  } : current),
                  () => setData((current) => current ? {
                    ...current,
                    deep_work_sessions: current.deep_work_sessions.map((item) => item.id === session.id ? session : item)
                  } : current),
                  action === "pause" ? "Đã pause" : action === "resume" ? "Tiếp tục Deep Work" : "Đã lưu Deep Work session"
                );
              }}
              onDeleteDeepWork={(session) => {
                setData((current) => current ? {
                  ...current,
                  deep_work_sessions: current.deep_work_sessions.filter((item) => item.id !== session.id)
                } : current);
                return commitOptimistic(
                  () => api<void>(`/api/deep-work/${session.id}`, { method: "DELETE" }),
                  () => undefined,
                  () => setData((current) => current ? {
                    ...current,
                    deep_work_sessions: [session, ...current.deep_work_sessions]
                  } : current),
                  "Đã xóa Deep Work session"
                );
              }}
              onAddDailyTask={(title) => {
                const timestamp = new Date().toISOString();
                const temp: DailyTask = {
                  id: `temp-${crypto.randomUUID()}`,
                  user_id: auth.user?.id || "",
                  work_date: localDateKey(),
                  title,
                  completed: 0,
                  position: Math.min(0, ...data.daily_tasks.filter((task) => task.work_date === localDateKey()).map((task) => task.position)) - 1,
                  parent_id: null,
                  created_at: timestamp,
                  updated_at: timestamp
                };
                setData((current) => current ? { ...current, daily_tasks: [temp, ...current.daily_tasks] } : current);
                return commitOptimistic(
                  () => api<DailyTask>("/api/daily-tasks", {
                    method: "POST",
                    body: JSON.stringify({ title, work_date: localDateKey() })
                  }),
                  (saved) => setData((current) => current ? {
                    ...current,
                    daily_tasks: current.daily_tasks.map((task) => task.id === temp.id ? saved : task)
                  } : current),
                  () => setData((current) => current ? {
                    ...current,
                    daily_tasks: current.daily_tasks.filter((task) => task.id !== temp.id)
                  } : current),
                  "Đã thêm task"
                );
              }}
              onToggleDailyTask={(task) => {
                const optimistic = { ...task, completed: (task.completed ? 0 : 1) as 0 | 1 };
                setData((current) => current ? {
                  ...current,
                  daily_tasks: current.daily_tasks.map((item) => item.id === task.id ? optimistic : item)
                } : current);
                return commitOptimistic(
                  () => api<DailyTask>(`/api/daily-tasks/${task.id}`, {
                    method: "PATCH",
                    body: JSON.stringify({ completed: !task.completed })
                  }),
                  (saved) => setData((current) => current ? {
                    ...current,
                    daily_tasks: current.daily_tasks.map((item) => item.id === saved.id ? saved : item)
                  } : current),
                  () => setData((current) => current ? {
                    ...current,
                    daily_tasks: current.daily_tasks.map((item) => item.id === task.id ? task : item)
                  } : current),
                  task.completed ? "Đã mở lại task" : "Đã hoàn thành task"
                );
              }}
              onEditDailyTask={(task, title) => {
                const optimistic = { ...task, title };
                setData((current) => current ? {
                  ...current,
                  daily_tasks: current.daily_tasks.map((item) => item.id === task.id ? optimistic : item)
                } : current);
                return commitOptimistic(
                  () => api<DailyTask>(`/api/daily-tasks/${task.id}`, {
                    method: "PATCH",
                    body: JSON.stringify({ title })
                  }),
                  (saved) => setData((current) => current ? {
                    ...current,
                    daily_tasks: current.daily_tasks.map((item) => item.id === saved.id ? saved : item)
                  } : current),
                  () => setData((current) => current ? {
                    ...current,
                    daily_tasks: current.daily_tasks.map((item) => item.id === task.id ? task : item)
                  } : current),
                  "Đã cập nhật task"
                );
              }}
              onIndentDailyTask={(task, parentId) => {
                const optimistic = { ...task, parent_id: parentId };
                setData((current) => current ? {
                  ...current,
                  daily_tasks: current.daily_tasks.map((item) => item.id === task.id ? optimistic : item)
                } : current);
                return commitOptimistic(
                  () => api<DailyTask>(`/api/daily-tasks/${task.id}`, {
                    method: "PATCH",
                    body: JSON.stringify({ parent_id: parentId })
                  }),
                  (saved) => setData((current) => current ? {
                    ...current,
                    daily_tasks: current.daily_tasks.map((item) => item.id === saved.id ? saved : item)
                  } : current),
                  () => setData((current) => current ? {
                    ...current,
                    daily_tasks: current.daily_tasks.map((item) => item.id === task.id ? task : item)
                  } : current),
                  parentId ? "Đã tạo task con" : "Đã đưa task ra cấp cha"
                );
              }}
              onDeleteDailyTask={(task) => {
                setData((current) => current ? {
                  ...current,
                  daily_tasks: current.daily_tasks.filter((item) => item.id !== task.id)
                } : current);
                return commitOptimistic(
                  () => api<void>(`/api/daily-tasks/${task.id}`, { method: "DELETE" }),
                  () => undefined,
                  () => setData((current) => current ? { ...current, daily_tasks: [...current.daily_tasks, task] } : current),
                  "Đã xóa task"
                );
              }}
              onReorderDailyTasks={(tasks) => {
                const previous = data.daily_tasks.filter((task) => task.work_date === localDateKey());
                const reordered = tasks.map((task, index) => ({ ...task, position: index + 1 }));
                const ids = new Set(reordered.map((task) => task.id));
                setData((current) => current ? {
                  ...current,
                  daily_tasks: [...current.daily_tasks.filter((task) => !ids.has(task.id)), ...reordered]
                } : current);
                return commitOptimistic(
                  () => api<DailyTask[]>("/api/daily-tasks/reorder", {
                    method: "POST",
                    body: JSON.stringify({ work_date: localDateKey(), task_ids: tasks.map((task) => task.id) })
                  }),
                  (saved) => {
                    const savedIds = new Set(saved.map((task) => task.id));
                    setData((current) => current ? {
                      ...current,
                      daily_tasks: [...current.daily_tasks.filter((task) => !savedIds.has(task.id)), ...saved]
                    } : current);
                  },
                  () => {
                    const previousIds = new Set(previous.map((task) => task.id));
                    setData((current) => current ? {
                      ...current,
                      daily_tasks: [...current.daily_tasks.filter((task) => !previousIds.has(task.id)), ...previous]
                    } : current);
                  },
                  "Đã sắp xếp task"
                );
              }}
            />
          )}
          {view === "production" && (
            <ProductionView
              data={data}
              filter={channelFilter}
              formatFilter={productionFormatFilter}
              search={search}
              onEdit={setVideoEdit}
              onMove={(video, status) => {
                const optimistic = { ...video, status };
                setData((current) => current ? {
                  ...current,
                  videos: current.videos.map((item) => item.id === video.id ? optimistic : item)
                } : current);
                void commitOptimistic(
                  () => api<Video>(`/api/videos/${video.id}`, {
                    method: "PATCH",
                    body: JSON.stringify({ status })
                  }),
                  (saved) => setData((current) => current ? {
                    ...current,
                    videos: current.videos.map((item) => item.id === saved.id ? saved : item)
                  } : current),
                  () => setData((current) => current ? {
                    ...current,
                    videos: current.videos.map((item) => item.id === video.id ? video : item)
                  } : current),
                  `Đã chuyển sang ${VIDEO_COLUMNS.find((column) => column.id === status)?.label}`
                ).catch(() => undefined);
              }}
            />
          )}
          {view === "channels" && (
            <ChannelsView
              data={data}
              search={channelSearch}
              formatFilter={channelFormatFilter}
              statusFilter={channelStatusFilter}
              onFormatFilter={setChannelFormatFilter}
              onStatusFilter={setChannelStatusFilter}
              onEdit={setChannelEdit}
              onFilterProduction={(channelId) => {
                setChannelFilter(channelId);
                setView("production");
              }}
            />
          )}
          {view === "niches" && (
            <NichesView
              niches={data.niches}
              references={data.niche_references}
              obsidianNotes={data.niche_obsidian_notes}
              patterns={data.ideation_patterns}
              mode={nicheLabMode}
              onModeChange={setNicheLabMode}
              search={nicheSearch}
              formatFilter={nicheFormatFilter}
              onFormatFilter={setNicheFormatFilter}
              onEdit={setNicheEdit}
              onEditPattern={setPatternEdit}
            />
          )}
          {view === "products" && (
            <ProductsView
              data={data}
              onEdit={setProductEdit}
              onMove={(product, status) => {
                const optimistic = { ...product, status };
                setData((current) => current ? {
                  ...current,
                  products: current.products.map((item) => item.id === product.id ? optimistic : item)
                } : current);
                void commitOptimistic(
                  () => api<Product>(`/api/products/${product.id}`, {
                    method: "PATCH",
                    body: JSON.stringify({ status })
                  }),
                  (saved) => setData((current) => current ? {
                    ...current,
                    products: current.products.map((item) => item.id === saved.id ? saved : item)
                  } : current),
                  () => setData((current) => current ? {
                    ...current,
                    products: current.products.map((item) => item.id === product.id ? product : item)
                  } : current),
                  "Đã chuyển product"
                ).catch(() => undefined);
              }}
            />
          )}
          {view === "heygen" && auth.user?.role === "admin" && (
            <HeyGenAccountsView
              accounts={heyGenAccounts}
              search={search}
              onEdit={setHeyGenEdit}
              onDelete={async (account) => {
                await api<void>(`/api/heygen-accounts/${account.id}`, { method: "DELETE" });
                setHeyGenAccounts((current) => current.filter((item) => item.id !== account.id));
                flash("Đã xóa HeyGen account");
              }}
              onError={(message) => setNotice(message)}
            />
          )}
          {view === "users" && auth.user?.role === "admin" && (
            <UsersView
              users={users}
              currentUserId={auth.user.id}
              onEdit={setUserEdit}
            />
          )}
        </section>
      </main>

      {notice && <div className="toast">{notice}</div>}

      {videoEdit && (
        <VideoModal
          value={videoEdit}
          data={data}
          onClose={() => setVideoEdit(null)}
          onSave={async (payload, pendingAssets) => {
            let videoId = videoEdit.id;
            try {
              let savedVideo: Video;
              if (videoEdit.id) {
                savedVideo = await api<Video>(`/api/videos/${videoEdit.id}`, {
                  method: "PATCH",
                  body: JSON.stringify(payload)
                });
              } else {
                savedVideo = await api<Video>("/api/videos", {
                  method: "POST",
                  body: JSON.stringify(payload)
                });
                videoId = savedVideo.id;
                setVideoEdit(savedVideo);
              }
              setData((current) => current ? {
                ...current,
                videos: current.videos.some((video) => video.id === savedVideo.id)
                  ? current.videos.map((video) => video.id === savedVideo.id ? savedVideo : video)
                  : [...current.videos, savedVideo]
              } : current);
              if (pendingAssets.length && videoId) {
                const form = new FormData();
                form.append("video_id", videoId);
                form.append("channel_id", String(payload.channel_id || ""));
                form.append("types", JSON.stringify(pendingAssets.map((asset) => asset.type)));
                pendingAssets.forEach((asset) => form.append("files", asset.file));
                const assets = await api<Asset[]>("/api/uploads/batch", { method: "POST", body: form });
                setData((current) => current ? {
                  ...current,
                  assets: [...assets, ...current.assets]
                } : current);
              }
              setVideoEdit(null);
              flash(videoEdit.id ? "Đã lưu video" : "Đã thêm video");
            } catch (error) {
              setNotice(error instanceof Error ? error.message : "Có lỗi xảy ra");
              throw error;
            }
          }}
          onDelete={
            videoEdit.id
              ? async () => {
                  if (!window.confirm("Xóa video card và toàn bộ file đính kèm?")) return;
                  const id = videoEdit.id as string;
                  try {
                    await api<void>(`/api/videos/${id}`, { method: "DELETE" });
                    setData((current) => current ? {
                      ...current,
                      videos: current.videos.filter((video) => video.id !== id),
                      assets: current.assets.filter((asset) => asset.video_id !== id)
                    } : current);
                    setVideoEdit(null);
                    flash("Đã xóa video");
                  } catch (error) {
                    setNotice(error instanceof Error ? error.message : "Có lỗi xảy ra");
                    throw error;
                  }
                }
              : undefined
          }
          onDeleteAsset={async (asset) => {
            if (!window.confirm(`Xóa file "${asset.name}"?`)) return;
            await api<void>(`/api/assets/${asset.id}`, { method: "DELETE" });
            setData((current) => current ? {
              ...current,
              assets: current.assets.filter((item) => item.id !== asset.id)
            } : current);
            flash("Đã xóa file");
          }}
        />
      )}

      {channelEdit && (
        <ChannelModal
          value={channelEdit}
          references={data.channel_references.filter(
            (reference) => reference.channel_id === channelEdit.id
          )}
          topicSources={data.channel_topic_sources.filter(
            (source) => source.channel_id === channelEdit.id
          )}
          onClose={() => setChannelEdit(null)}
          onSave={async (payload) => {
            try {
              const saved = await api<Channel>(channelEdit.id ? `/api/channels/${channelEdit.id}` : "/api/channels", {
                method: channelEdit.id ? "PATCH" : "POST",
                body: JSON.stringify(payload)
              });
              const timestamp = new Date().toISOString();
              const savedReferences: ChannelReference[] = (Array.isArray(payload.references) ? payload.references : [])
                .map((reference, index) => ({
                  id: `local-${crypto.randomUUID()}`,
                  channel_id: saved.id,
                  name: String((reference as { name?: unknown }).name || ""),
                  url: String((reference as { url?: unknown }).url || ""),
                  position: index + 1,
                  created_at: timestamp,
                  updated_at: timestamp
                }))
                .filter((reference) => reference.name || reference.url);
              const savedTopicSources: ChannelTopicSource[] = (Array.isArray(payload.topic_sources) ? payload.topic_sources : [])
                .map((source, index) => ({
                  id: `local-${crypto.randomUUID()}`,
                  channel_id: saved.id,
                  name: String((source as { name?: unknown }).name || ""),
                  url: String((source as { url?: unknown }).url || ""),
                  notes: String((source as { notes?: unknown }).notes || ""),
                  position: index + 1,
                  created_at: timestamp,
                  updated_at: timestamp
                }))
                .filter((source) => source.name || source.url || source.notes);
              setData((current) => current ? {
                ...current,
                channels: current.channels.some((channel) => channel.id === saved.id)
                  ? current.channels.map((channel) => channel.id === saved.id ? saved : channel)
                  : [...current.channels, saved],
                channel_references: [
                  ...current.channel_references.filter((reference) => reference.channel_id !== saved.id),
                  ...savedReferences
                ],
                channel_topic_sources: [
                  ...current.channel_topic_sources.filter((source) => source.channel_id !== saved.id),
                  ...savedTopicSources
                ]
              } : current);
              setChannelEdit(null);
              flash(channelEdit.id ? "Đã lưu channel" : "Đã tạo channel");
            } catch (error) {
              setNotice(error instanceof Error ? error.message : "Có lỗi xảy ra");
              throw error;
            }
          }}
          onDelete={
            channelEdit.id
              ? async () => {
                  if (!window.confirm("Xóa channel này cùng video, assets và metrics liên quan?")) return;
                  const id = channelEdit.id as string;
                  await api<void>(`/api/channels/${id}`, { method: "DELETE" });
                  setData((current) => current ? {
                    ...current,
                    channels: current.channels.filter((channel) => channel.id !== id),
                    channel_references: current.channel_references.filter((reference) => reference.channel_id !== id),
                    channel_topic_sources: current.channel_topic_sources.filter((source) => source.channel_id !== id),
                    videos: current.videos.filter((video) => video.channel_id !== id),
                    assets: current.assets.filter((asset) => asset.channel_id !== id),
                    metrics: current.metrics.filter((metric) => metric.channel_id !== id)
                  } : current);
                  setChannelEdit(null);
                  flash("Đã xóa channel");
                }
              : undefined
          }
        />
      )}

      {nicheEdit && (
        <NicheModal
          value={nicheEdit}
          references={data.niche_references.filter(
            (reference) => reference.niche_id === nicheEdit.id
          )}
          obsidianNotes={data.niche_obsidian_notes.filter(
            (note) => note.niche_id === nicheEdit.id
          )}
          onClose={() => setNicheEdit(null)}
          onSave={async (payload) => {
            try {
              const saved = await api<Niche>(nicheEdit.id ? `/api/niches/${nicheEdit.id}` : "/api/niches", {
                method: nicheEdit.id ? "PATCH" : "POST",
                body: JSON.stringify(payload)
              });
              const timestamp = new Date().toISOString();
              const savedReferences: NicheReference[] = (Array.isArray(payload.references) ? payload.references : [])
                .map((reference, index) => ({
                  id: `local-${crypto.randomUUID()}`,
                  niche_id: saved.id,
                  name: String((reference as { name?: unknown }).name || ""),
                  url: String((reference as { url?: unknown }).url || ""),
                  position: index + 1,
                  created_at: timestamp,
                  updated_at: timestamp
                }))
                .filter((reference) => reference.name || reference.url);
              const savedNotes: NicheObsidianNote[] = (Array.isArray(payload.obsidian_notes) ? payload.obsidian_notes : [])
                .map((note, index) => ({
                  id: `local-${crypto.randomUUID()}`,
                  niche_id: saved.id,
                  name: String((note as { name?: unknown }).name || ""),
                  note_path: String((note as { note_path?: unknown }).note_path || ""),
                  position: index + 1,
                  created_at: timestamp,
                  updated_at: timestamp
                }))
                .filter((note) => note.name || note.note_path);
              setData((current) => current ? {
                ...current,
                niches: current.niches.some((niche) => niche.id === saved.id)
                  ? current.niches.map((niche) => niche.id === saved.id ? saved : niche)
                  : [...current.niches, saved],
                niche_references: [
                  ...current.niche_references.filter((reference) => reference.niche_id !== saved.id),
                  ...savedReferences
                ],
                niche_obsidian_notes: [
                  ...current.niche_obsidian_notes.filter((note) => note.niche_id !== saved.id),
                  ...savedNotes
                ]
              } : current);
              setNicheEdit(null);
              flash(nicheEdit.id ? "Đã lưu niche" : "Đã thêm niche");
            } catch (error) {
              setNotice(error instanceof Error ? error.message : "Có lỗi xảy ra");
              throw error;
            }
          }}
          onDelete={
            nicheEdit.id
              ? async () => {
                  if (!window.confirm("Xóa niche idea này?")) return;
                  const id = nicheEdit.id as string;
                  await api<void>(`/api/niches/${id}`, { method: "DELETE" });
                  setData((current) => current ? {
                    ...current,
                    niches: current.niches.filter((niche) => niche.id !== id),
                    niche_references: current.niche_references.filter((reference) => reference.niche_id !== id),
                    niche_obsidian_notes: current.niche_obsidian_notes.filter((note) => note.niche_id !== id)
                  } : current);
                  setNicheEdit(null);
                  flash("Đã xóa niche");
                }
              : undefined
          }
        />
      )}

      {patternEdit && (
        <IdeationPatternModal
          value={patternEdit}
          onClose={() => setPatternEdit(null)}
          onSave={async (payload) => {
            try {
              const saved = await api<IdeationPattern>(
                patternEdit.id
                  ? `/api/ideation-patterns/${patternEdit.id}`
                  : "/api/ideation-patterns",
                {
                  method: patternEdit.id ? "PATCH" : "POST",
                  body: JSON.stringify(payload)
                }
              );
              setData((current) => current ? {
                ...current,
                ideation_patterns: current.ideation_patterns.some((pattern) => pattern.id === saved.id)
                  ? current.ideation_patterns.map((pattern) => pattern.id === saved.id ? saved : pattern)
                  : [saved, ...current.ideation_patterns]
              } : current);
              setPatternEdit(null);
              flash(patternEdit.id ? "Đã lưu pattern" : "Đã thêm pattern");
            } catch (error) {
              setNotice(error instanceof Error ? error.message : "Có lỗi xảy ra");
              throw error;
            }
          }}
          onDelete={
            patternEdit.id
              ? async () => {
                  if (!window.confirm("Xóa ideation pattern này?")) return;
                  const id = patternEdit.id as string;
                  await api<void>(`/api/ideation-patterns/${id}`, { method: "DELETE" });
                  setData((current) => current ? {
                    ...current,
                    ideation_patterns: current.ideation_patterns.filter((pattern) => pattern.id !== id)
                  } : current);
                  setPatternEdit(null);
                  flash("Đã xóa pattern");
                }
              : undefined
          }
        />
      )}

      {productEdit && (
        <ProductModal
          value={productEdit}
          channels={data.channels}
          onClose={() => setProductEdit(null)}
          onSave={async (payload) => {
            try {
              const saved = await api<Product>(productEdit.id ? `/api/products/${productEdit.id}` : "/api/products", {
                method: productEdit.id ? "PATCH" : "POST",
                body: JSON.stringify(payload)
              });
              setData((current) => current ? {
                ...current,
                products: current.products.some((product) => product.id === saved.id)
                  ? current.products.map((product) => product.id === saved.id ? saved : product)
                  : [...current.products, saved]
              } : current);
              setProductEdit(null);
              flash(productEdit.id ? "Đã lưu product" : "Đã thêm product");
            } catch (error) {
              setNotice(error instanceof Error ? error.message : "Có lỗi xảy ra");
              throw error;
            }
          }}
          onDelete={
            productEdit.id
              ? async () => {
                  if (!window.confirm("Xóa product này cùng assets liên quan?")) return;
                  const id = productEdit.id as string;
                  await api<void>(`/api/products/${id}`, { method: "DELETE" });
                  setData((current) => current ? {
                    ...current,
                    products: current.products.filter((product) => product.id !== id),
                    assets: current.assets.filter((asset) => asset.product_id !== id)
                  } : current);
                  setProductEdit(null);
                  flash("Đã xóa product");
                }
              : undefined
          }
        />
      )}

      {userEdit && auth.user?.role === "admin" && (
        <UserModal
          value={userEdit}
          currentUserId={auth.user.id}
          onClose={() => setUserEdit(null)}
          onSave={async (payload) => {
            try {
              const saved = await api<User>(userEdit.id ? `/api/users/${userEdit.id}` : "/api/users", {
                method: userEdit.id ? "PATCH" : "POST",
                body: JSON.stringify(payload)
              });
              setUsers((current) => current.some((user) => user.id === saved.id)
                ? current.map((user) => user.id === saved.id ? saved : user)
                : [...current, saved]);
              if (userEdit.id === auth.user?.id) {
                setAuth((current) => current ? { ...current, user: saved } : current);
              }
              setUserEdit(null);
              flash(userEdit.id ? "Đã cập nhật tài khoản" : "Đã tạo tài khoản mới");
            } catch (error) {
              setNotice(error instanceof Error ? error.message : "Có lỗi xảy ra");
              throw error;
            }
          }}
        />
      )}

      {heyGenEdit && auth.user?.role === "admin" && (
        <HeyGenAccountModal
          value={heyGenEdit}
          onClose={() => setHeyGenEdit(null)}
          onSave={async (payload) => {
            try {
              const saved = await api<HeyGenAccount>(
                heyGenEdit.id ? `/api/heygen-accounts/${heyGenEdit.id}` : "/api/heygen-accounts",
                {
                  method: heyGenEdit.id ? "PATCH" : "POST",
                  body: JSON.stringify(payload)
                }
              );
              setHeyGenAccounts((current) => current.some((account) => account.id === saved.id)
                ? current.map((account) => account.id === saved.id ? saved : account)
                : [...current, saved]);
              setHeyGenEdit(null);
              flash(heyGenEdit.id ? "Đã cập nhật HeyGen account" : "Đã thêm HeyGen account");
            } catch (error) {
              setNotice(error instanceof Error ? error.message : "Có lỗi xảy ra");
              throw error;
            }
          }}
          onDelete={
            heyGenEdit.id
              ? async () => {
                  if (!window.confirm(`Xóa HeyGen account #${heyGenEdit.account_number}?`)) return;
                  const id = heyGenEdit.id as string;
                  await api<void>(`/api/heygen-accounts/${id}`, { method: "DELETE" });
                  setHeyGenAccounts((current) => current.filter((account) => account.id !== id));
                  setHeyGenEdit(null);
                  flash("Đã xóa HeyGen account");
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="brand-mark"><Flame size={24} /></div>
      <div>
        <strong>FORGE OS</strong>
        <span>Loading command center…</span>
      </div>
    </div>
  );
}

function LoginScreen({
  setupRequired,
  onLogin,
  onSetup,
  busy
}: {
  setupRequired: boolean;
  onLogin: (username: string, password: string) => Promise<void>;
  onSetup: (payload: {
    username: string;
    display_name: string;
    password: string;
  }) => Promise<void>;
  busy: boolean;
}) {
  const [username, setUsername] = useState(setupRequired ? "admin" : "");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");

  return (
    <div className="login-page">
      <div className="login-glow" />
      <form
        className="login-card"
        onSubmit={async (event) => {
          event.preventDefault();
          setError("");
          try {
            if (setupRequired) {
              if (password !== confirmation) {
                throw new Error("Hai password chưa khớp nhau");
              }
              await onSetup({
                username,
                display_name: displayName,
                password
              });
            } else {
              await onLogin(username, password);
            }
          } catch (err) {
            setError(err instanceof Error ? err.message : "Không thể đăng nhập");
          }
        }}
      >
        <div className="brand-mark large"><Flame size={28} /></div>
        <p className="eyebrow">{setupRequired ? "First-time setup" : "Private workspace"}</p>
        <h1>{setupRequired ? "Create owner account" : "Forge OS"}</h1>
        <p>
          {setupRequired
            ? "Tạo admin đầu tiên. Sau khi vào app, anh có thể tạo thêm account cho người cộng tác."
            : "Đăng nhập bằng account được admin cấp."}
        </p>
        {setupRequired && (
          <label>
            <span>Display name</span>
            <div className="password-field">
              <UserCog size={17} />
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                autoFocus
                required
                autoComplete="name"
              />
            </div>
          </label>
        )}
        <label>
          <span>Username</span>
          <div className="password-field">
            <Users size={17} />
            <input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoFocus={!setupRequired}
              required
              minLength={3}
              autoCapitalize="none"
              autoComplete="username"
            />
          </div>
        </label>
        <label>
          <span>Password</span>
          <div className="password-field">
            <LockKeyhole size={17} />
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={setupRequired ? 10 : undefined}
              autoComplete={setupRequired ? "new-password" : "current-password"}
            />
          </div>
        </label>
        {setupRequired && (
          <label>
            <span>Confirm password</span>
            <div className="password-field">
              <LockKeyhole size={17} />
              <input
                type="password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                required
                minLength={10}
                autoComplete="new-password"
              />
            </div>
          </label>
        )}
        {error && <div className="form-error">{error}</div>}
        <button className="primary-button wide" disabled={busy}>
          {busy ? <LoaderCircle size={16} /> : <ArrowRight size={16} />}
          {setupRequired ? "Create admin & enter" : "Enter Command Center"}
        </button>
      </form>
    </div>
  );
}

function Sidebar({
  view,
  setView,
  open,
  onClose,
  collapsed,
  onToggleCollapsed,
  data,
  currentUser,
  logout
}: {
  view: ViewName;
  setView: (view: ViewName) => void;
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  data: BootstrapData;
  currentUser: User | null;
  logout: () => void;
}) {
  const nav = [
    { id: "dashboard" as const, label: "Overview", icon: LayoutDashboard },
    { id: "production" as const, label: "Production", icon: KanbanSquare },
    { id: "handoff" as const, label: "Bàn giao", icon: FileText },
    { id: "channels" as const, label: "Channels", icon: Users },
    { id: "niches" as const, label: "Niche Lab", icon: Lightbulb },
    { id: "products" as const, label: "Monetization", icon: CircleDollarSign },
    ...(currentUser?.role === "admin"
      ? [
          { id: "heygen" as const, label: "HeyGen Accounts", icon: LockKeyhole },
          { id: "users" as const, label: "Users", icon: UserCog }
        ]
      : [])
  ];
  const totalTarget = data.channels.reduce((sum, channel) => sum + channel.target_videos, 0);
  const posted = data.videos.filter((video) => video.status === "posted").length;
  const percent = totalTarget ? Math.round((posted / totalTarget) * 100) : 0;

  return (
    <>
      {open && <button className="sidebar-scrim" onClick={onClose} aria-label="Close navigation" />}
      <aside className={`sidebar ${open ? "is-open" : ""}`}>
        <div className="sidebar-brand">
          <div className="brand-mark"><Flame size={22} /></div>
          <div className="sidebar-brand-copy">
            <strong>FORGE OS</strong>
            <span>Media Studio Operations</span>
          </div>
          <button
            className="icon-button sidebar-collapse-button"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
          <button className="icon-button sidebar-close" onClick={onClose}>
            <X size={17} />
          </button>
        </div>

        <nav>
          <p className="nav-label">Workspace</p>
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={view === item.id ? "active" : ""}
                onClick={() => setView(item.id)}
                title={collapsed ? item.label : undefined}
              >
                <Icon size={18} />
                <span>{item.label}</span>
                {item.id === "production" && (
                  <em>{data.videos.filter((video) => !["posted", "archived"].includes(video.status)).length}</em>
                )}
              </button>
            );
          })}
        </nav>

        <div className="challenge-card">
          <div className="challenge-card-head">
            <span><Target size={16} /> August batch</span>
            <strong>{percent}%</strong>
          </div>
          <div className="progress-track"><span style={{ width: `${percent}%` }} /></div>
          <p>{posted} / {totalTarget} videos posted</p>
        </div>

        <div className="sidebar-bottom">
          <div className="signed-in-user" title={collapsed ? currentUser?.display_name : undefined}>
            <span>{initials(currentUser?.display_name || currentUser?.username || "U")}</span>
            <div>
              <strong>{currentUser?.display_name}</strong>
              <small>{currentUser?.role === "admin" ? "Admin" : "Member"}</small>
            </div>
          </div>
          <a href="/api/export" className="sidebar-action" title={collapsed ? "Export backup" : undefined}>
            <Download size={17} /> <span>Export backup</span>
          </a>
          <button className="sidebar-action" onClick={logout} title={collapsed ? "Log out" : undefined}>
            <LogOut size={17} /> <span>Log out</span>
          </button>
        </div>
      </aside>
    </>
  );
}

function DashboardView({
  data,
  onOpenChannel,
  onGoProduction,
  onAddVideo,
  onStartDeepWork,
  onDeepWorkAction,
  onDeleteDeepWork,
  onAddDailyTask,
  onToggleDailyTask,
  onEditDailyTask,
  onIndentDailyTask,
  onDeleteDailyTask,
  onReorderDailyTasks
}: {
  data: BootstrapData;
  onOpenChannel: (channel: Channel) => void;
  onGoProduction: () => void;
  onAddVideo: () => void;
  onStartDeepWork: () => Promise<unknown>;
  onDeepWorkAction: (
    session: DeepWorkSession,
    action: "pause" | "resume" | "stop"
  ) => Promise<unknown>;
  onDeleteDeepWork: (session: DeepWorkSession) => Promise<unknown>;
  onAddDailyTask: (title: string) => Promise<unknown>;
  onToggleDailyTask: (task: DailyTask) => Promise<unknown>;
  onEditDailyTask: (task: DailyTask, title: string) => Promise<unknown>;
  onIndentDailyTask: (task: DailyTask, parentId: string | null) => Promise<unknown>;
  onDeleteDailyTask: (task: DailyTask) => Promise<unknown>;
  onReorderDailyTasks: (tasks: DailyTask[]) => Promise<unknown>;
}) {
  const activeVideos = data.videos.filter(
    (video) => !["posted", "archived"].includes(video.status)
  ).length;
  const posted = data.videos.filter((video) => video.status === "posted").length;
  const setupAverage = data.channels.length
    ? Math.round(
        data.channels.reduce((sum, channel) => sum + readiness(channel), 0) /
          data.channels.length
      )
    : 0;
  const liveProducts = data.products.filter((product) =>
    ["live", "scaling"].includes(product.status)
  ).length;
  const start = CAMPAIGN_START_DATE ? new Date(CAMPAIGN_START_DATE) : null;
  const now = new Date();
  const rawDay =
    start && !Number.isNaN(start.getTime())
      ? Math.floor((now.getTime() - start.getTime()) / 86400000) + 1
      : 0;
  const challengeDay = Math.max(0, Math.min(30, rawDay));
  const lowReadiness = [...data.channels]
    .sort((a, b) => readiness(a) - readiness(b))
    .slice(0, 3);
  const nextVideos = data.videos
    .filter((video) => !["posted", "archived"].includes(video.status))
    .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)))
    .slice(0, 4);

  return (
    <div className="dashboard-grid">
      <section className="hero-panel">
        <div className="hero-copy">
          <p className="eyebrow hero-mantra">
            <Flame size={18} /> Just does stuff. No waiting around.
          </p>
          <h2>1st August challenge begins!</h2>
          <div className="hero-actions">
            <button className="primary-button" onClick={onAddVideo}>
              <Plus size={16} /> Queue next video
            </button>
            <button className="secondary-button" onClick={onGoProduction}>
              Open production <ArrowRight size={15} />
            </button>
          </div>
        </div>
        <div className="challenge-orbit">
          <div className="orbit-ring">
            <span>{challengeDay || "—"}</span>
            <small>/ 30 days</small>
          </div>
          <div className="orbit-label">
            <CalendarDays size={14} />
            01 Aug → 31 Aug
          </div>
        </div>
      </section>

      <section className="focus-workspace">
        <div className="focus-tools">
          <DailyTasksPanel
            tasks={data.daily_tasks}
            onAdd={onAddDailyTask}
            onToggle={onToggleDailyTask}
            onEdit={onEditDailyTask}
            onIndent={onIndentDailyTask}
            onDelete={onDeleteDailyTask}
            onReorder={onReorderDailyTasks}
          />
          <DeepWorkPanel
            sessions={data.deep_work_sessions}
            onStart={onStartDeepWork}
            onAction={onDeepWorkAction}
            onDelete={onDeleteDeepWork}
          />
        </div>
      </section>

      <div className="metric-row">
        <MetricCard
          icon={<Users size={18} />}
          label="Launch setup"
          value={`${setupAverage}%`}
          detail={`${data.channels.length} channels trong batch`}
          tone="amber"
        />
        <MetricCard
          icon={<VideoIcon size={18} />}
          label="Active production"
          value={String(activeVideos)}
          detail={`${posted} videos đã post`}
          tone="blue"
        />
        <MetricCard
          icon={<Lightbulb size={18} />}
          label="Niche pipeline"
          value={String(data.niches.length)}
          detail={`${data.niches.filter((niche) => niche.status === "launch_ready").length} queued to launch`}
          tone="purple"
        />
        <MetricCard
          icon={<CircleDollarSign size={18} />}
          label="Products live"
          value={String(liveProducts)}
          detail={`${data.products.length} products đang track`}
          tone="green"
        />
      </div>

      <section className="panel setup-panel">
        <PanelHeader
          icon={<Rocket size={18} />}
          title="Launch setup progress"
          subtitle="Các bước setup còn thiếu cần hoàn tất trước khi launch"
          action={<button className="text-button" onClick={() => onOpenChannel(lowReadiness[0])}>Open next <ChevronRight size={14} /></button>}
        />
        <div className="readiness-list">
          {data.channels.map((channel) => {
            const score = readiness(channel);
            return (
              <button
                className="readiness-item"
                key={channel.id}
                onClick={() => onOpenChannel(channel)}
              >
                <ChannelAvatar channel={channel} />
                <div className="readiness-info">
                  <div><strong>{channel.name}</strong><span>{channel.host_name}</span></div>
                  <div className="mini-progress">
                    <span style={{ width: `${score}%`, background: channel.color }} />
                  </div>
                </div>
                <b>{score}%</b>
              </button>
            );
          })}
        </div>
      </section>

      <section className="panel queue-panel">
        <PanelHeader
          icon={<KanbanSquare size={18} />}
          title="Next in queue"
          subtitle="Việc gần nhất cần được đẩy qua board"
          action={<button className="text-button" onClick={onGoProduction}>View board <ChevronRight size={14} /></button>}
        />
        <div className="queue-list">
          {nextVideos.map((video) => {
            const channel = data.channels.find((item) => item.id === video.channel_id);
            const column = VIDEO_COLUMNS.find((item) => item.id === video.status);
            return (
              <button key={video.id} className="queue-item" onClick={onGoProduction}>
                <span className="queue-dot" style={{ background: column?.color }} />
                <div>
                  <strong>{video.title}</strong>
                  <span>{channel?.name || "Unknown channel"}</span>
                </div>
                <time>{formatDate(video.due_date)}</time>
              </button>
            );
          })}
          {!nextVideos.length && (
            <EmptyCompact text="Chưa có video nào trong queue." />
          )}
        </div>
      </section>

      <section className="panel decision-panel">
        <PanelHeader
          icon={<Sparkles size={18} />}
          title="Operating rules"
          subtitle="Giữ tốc độ mà không biến test thành chaos"
        />
        <div className="rule-grid">
          <div><span>01</span><p><strong>Demand before creativity</strong> Mỗi card phải có proven topic hoặc demand evidence.</p></div>
          <div><span>02</span><p><strong>Decision → output in 24h</strong> Niche đã chốt phải thành setup hoặc video queue.</p></div>
          <div><span>03</span><p><strong>Product after signal</strong> Sau 7–10 ngày, đưa ebook vào Monetization Lab.</p></div>
        </div>
      </section>
    </div>
  );
}

function DeepWorkPanel({
  sessions,
  onStart,
  onAction,
  onDelete
}: {
  sessions: DeepWorkSession[];
  onStart: () => Promise<unknown>;
  onAction: (
    session: DeepWorkSession,
    action: "pause" | "resume" | "stop"
  ) => Promise<unknown>;
  onDelete: (session: DeepWorkSession) => Promise<unknown>;
}) {
  const [nowMs, setNowMs] = useState(Date.now());
  const activeSession = sessions.find((session) =>
    ["running", "paused"].includes(session.status)
  );
  const today = localDateKey();
  const todaySessions = [...sessions]
    .filter((session) => session.work_date === today)
    .sort((left, right) => left.started_at.localeCompare(right.started_at));
  const todayTotal = todaySessions.reduce(
    (sum, session) => sum + sessionSeconds(session, nowMs),
    0
  );
  const weekStart = localWeekStartKey();
  const monthPrefix = today.slice(0, 7);
  const weekSessions = sessions.filter(
    (session) => session.work_date >= weekStart && session.work_date <= today
  );
  const monthSessions = sessions.filter((session) =>
    session.work_date.startsWith(monthPrefix)
  );
  const weekTotal = weekSessions.reduce(
    (sum, session) => sum + sessionSeconds(session, nowMs),
    0
  );
  const monthTotal = monthSessions.reduce(
    (sum, session) => sum + sessionSeconds(session, nowMs),
    0
  );

  useEffect(() => {
    if (activeSession?.status !== "running") return;
    const timerId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timerId);
  }, [activeSession?.id, activeSession?.status]);

  const run = (action: () => Promise<unknown>) => {
    void action().catch(() => undefined);
  };

  return (
    <section className="deep-work-panel">
      <div className="deep-work-head">
        <div>
          <p className="eyebrow"><Timer size={14} /> Deep Work today</p>
          <strong>{formatWorkTotal(todayTotal)}</strong>
        </div>
        <span>{todaySessions.length} sessions</span>
      </div>

      <div className={`timer-console ${activeSession?.status || "idle"}`}>
        <div className="timer-state">
          <i />
          {activeSession?.status === "running"
            ? "Deep work in progress"
            : activeSession?.status === "paused"
              ? "Paused"
              : "Ready"}
        </div>
        <time>
          {activeSession
            ? formatTimer(sessionSeconds(activeSession, nowMs))
            : "00:00:00"}
        </time>
        <div className="timer-actions">
          {!activeSession && (
            <button
              className="timer-start"
              onClick={() => run(onStart)}
            >
              <Play size={17} fill="currentColor" /> Start
            </button>
          )}
          {activeSession?.status === "running" && (
            <button
              className="timer-pause"
              disabled={activeSession.id.startsWith("temp-")}
              onClick={() => run(() => onAction(activeSession, "pause"))}
            >
              <Pause size={17} fill="currentColor" /> Pause
            </button>
          )}
          {activeSession?.status === "paused" && (
            <button
              className="timer-start"
              onClick={() => run(() => onAction(activeSession, "resume"))}
            >
              <Play size={17} fill="currentColor" /> Resume
            </button>
          )}
          {activeSession && (
            <button
              className="timer-stop"
              disabled={activeSession.id.startsWith("temp-")}
              onClick={() => run(() => onAction(activeSession, "stop"))}
            >
              <Square size={15} fill="currentColor" /> Stop & save
            </button>
          )}
        </div>
      </div>

      <div className="deep-work-totals">
        <div>
          <span><CalendarDays size={13} /> This week</span>
          <strong>{formatWorkTotal(weekTotal)}</strong>
          <small>{weekSessions.length} sessions · từ thứ Hai</small>
        </div>
        <div>
          <span><BarChart3 size={13} /> This month</span>
          <strong>{formatWorkTotal(monthTotal)}</strong>
          <small>{monthSessions.length} sessions · tháng này</small>
        </div>
      </div>

      <div className="deep-work-records">
        <div className="record-heading">
          <span>Today's record</span>
          <span>Duration</span>
        </div>
        {todaySessions.map((session, index) => (
          <div className="deep-work-record" key={session.id}>
            <div className="deep-work-record-copy">
              <strong>Deep Work {index + 1}</strong>
              <span>
                {new Intl.DateTimeFormat("vi-VN", {
                  hour: "2-digit",
                  minute: "2-digit"
                }).format(new Date(session.started_at))}
                {" → "}
                {session.ended_at
                  ? new Intl.DateTimeFormat("vi-VN", {
                      hour: "2-digit",
                      minute: "2-digit"
                    }).format(new Date(session.ended_at))
                  : session.status === "paused"
                    ? "paused"
                    : "now"}
              </span>
            </div>
            <div className="deep-work-record-actions">
              <time>{formatTimer(sessionSeconds(session, nowMs))}</time>
              <button
                type="button"
                onClick={() => {
                  const detail = session.status === "completed"
                    ? "Session này sẽ bị xóa khỏi tổng Deep Work hôm nay."
                    : "Session này đang chạy hoặc pause; xóa sẽ hủy timer hiện tại.";
                  if (!window.confirm(`Xóa Deep Work ${index + 1}?\n\n${detail}`)) return;
                  run(() => onDelete(session));
                }}
                aria-label={`Delete Deep Work ${index + 1}`}
                title="Delete session"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {!todaySessions.length && (
          <EmptyCompact text="Chưa có Deep Work session hôm nay." />
        )}
      </div>
    </section>
  );
}

function DailyTasksPanel({
  tasks,
  onAdd,
  onToggle,
  onEdit,
  onIndent,
  onDelete,
  onReorder
}: {
  tasks: DailyTask[];
  onAdd: (title: string) => Promise<unknown>;
  onToggle: (task: DailyTask) => Promise<unknown>;
  onEdit: (task: DailyTask, title: string) => Promise<unknown>;
  onIndent: (task: DailyTask, parentId: string | null) => Promise<unknown>;
  onDelete: (task: DailyTask) => Promise<unknown>;
  onReorder: (tasks: DailyTask[]) => Promise<unknown>;
}) {
  const [title, setTitle] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  const tasksForToday = tasks.filter((task) => task.work_date === localDateKey());
  const taskIds = new Set(tasksForToday.map((task) => task.id));
  const sortTasks = (left: DailyTask, right: DailyTask) =>
      right.completed - left.completed ||
      left.position - right.position ||
      left.created_at.localeCompare(right.created_at);
  const rootTasks = tasksForToday
    .filter((task) => !task.parent_id || !taskIds.has(task.parent_id))
    .sort(sortTasks);
  const todayTasks = rootTasks.flatMap((parent) => [
    parent,
    ...tasksForToday
      .filter((task) => task.parent_id === parent.id)
      .sort(sortTasks)
  ]);

  function handleDragEnd(event: DragEndEvent) {
    const activeId = String(event.active.id);
    const overId = String(event.over?.id || "");
    if (!overId || activeId === overId) return;
    const fromIndex = todayTasks.findIndex((task) => task.id === activeId);
    const toIndex = todayTasks.findIndex((task) => task.id === overId);
    if (fromIndex < 0 || toIndex < 0) return;
    if (todayTasks[fromIndex].completed !== todayTasks[toIndex].completed) return;
    if (todayTasks[fromIndex].parent_id !== todayTasks[toIndex].parent_id) return;
    const reordered = [...todayTasks];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    void onReorder(reordered).catch(() => undefined);
  }

  return (
    <section className="daily-tasks-panel">
      <div className="daily-tasks-head">
        <div>
          <p className="eyebrow"><ListTodo size={14} /> Today's tasks</p>
          <strong>What needs to get done?</strong>
        </div>
        <span>{todayTasks.filter((task) => task.completed).length}/{todayTasks.length}</span>
      </div>
      <form
        className="daily-task-input"
        onSubmit={async (event) => {
          event.preventDefault();
          const cleanTitle = title.trim();
          if (!cleanTitle) return;
          setTitle("");
          try {
            await onAdd(cleanTitle);
          } catch {
            setTitle((current) => current || cleanTitle);
          }
        }}
      >
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Add a task for today..."
          aria-label="Task for today"
        />
        <button disabled={!title.trim()}><Plus size={16} /> Add</button>
      </form>
      <small className="daily-task-shortcut">Focus task + Tab để thụt vào · Shift + Tab để đưa ra</small>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div className="daily-task-list">
          {todayTasks.map((task) => (
            <DraggableDailyTask
              key={task.id}
              task={task}
              editing={editingId === task.id}
              editTitle={editTitle}
              onEditTitle={setEditTitle}
              onToggle={onToggle}
              onEdit={onEdit}
              onIndent={onIndent}
              onDelete={onDelete}
              suggestedParentId={(() => {
                if (task.parent_id) return null;
                const index = todayTasks.findIndex((item) => item.id === task.id);
                const previous = index > 0 ? todayTasks[index - 1] : undefined;
                if (!previous || previous.completed !== task.completed) return null;
                return previous.parent_id || previous.id;
              })()}
              onStartEditing={() => {
                setEditingId(task.id);
                setEditTitle(task.title);
              }}
              onCancelEditing={() => {
                setEditingId(null);
                setEditTitle("");
              }}
              onFinishEditing={() => {
                setEditingId(null);
                setEditTitle("");
              }}
            />
          ))}
          {!todayTasks.length && <EmptyCompact text="Chưa có task hôm nay." />}
        </div>
      </DndContext>
    </section>
  );
}

function DraggableDailyTask({
  task,
  editing,
  editTitle,
  onEditTitle,
  onToggle,
  onEdit,
  onIndent,
  onDelete,
  suggestedParentId,
  onStartEditing,
  onCancelEditing,
  onFinishEditing
}: {
  task: DailyTask;
  editing: boolean;
  editTitle: string;
  onEditTitle: (title: string) => void;
  onToggle: (task: DailyTask) => Promise<unknown>;
  onEdit: (task: DailyTask, title: string) => Promise<unknown>;
  onIndent: (task: DailyTask, parentId: string | null) => Promise<unknown>;
  onDelete: (task: DailyTask) => Promise<unknown>;
  suggestedParentId: string | null;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onFinishEditing: () => void;
}) {
  const pending = task.id.startsWith("temp-");
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    transform,
    isDragging
  } = useDraggable({ id: task.id, disabled: pending });
  const {
    setNodeRef: setDroppableRef,
    isOver
  } = useDroppable({ id: task.id });
  const style: CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : {};

  return (
    <div
      ref={(node) => {
        setDraggableRef(node);
        setDroppableRef(node);
      }}
      style={style}
      className={[
        "daily-task",
        task.parent_id ? "child" : "",
        task.completed ? "completed" : "",
        isDragging ? "dragging" : "",
        isOver && !isDragging ? "drag-over" : ""
      ].filter(Boolean).join(" ")}
      tabIndex={pending ? -1 : 0}
      aria-level={task.parent_id ? 2 : 1}
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (!target.closest("button, input, form")) event.currentTarget.focus();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Tab" || pending) return;
        const nextParentId = event.shiftKey ? null : suggestedParentId;
        const canChange = event.shiftKey
          ? Boolean(task.parent_id)
          : !task.parent_id && Boolean(nextParentId);
        if (!canChange) return;
        event.preventDefault();
        void onIndent(task, nextParentId).catch(() => undefined);
      }}
    >
      <button
        className="task-drag-handle"
        disabled={pending}
        {...listeners}
        {...attributes}
        aria-label={`Move ${task.title}`}
        title="Drag to reorder"
      >
        <GripVertical size={16} />
      </button>
      <button
        className="task-check"
        disabled={pending}
        onClick={() => void onToggle(task).catch(() => undefined)}
        aria-label={task.completed ? "Mark task incomplete" : "Mark task complete"}
      >
        {task.completed === 1 && <Check size={13} />}
      </button>
      {editing ? (
        <form
          className="task-edit-form"
          onSubmit={async (event) => {
            event.preventDefault();
            const cleanTitle = editTitle.trim();
            if (!cleanTitle) return;
            onFinishEditing();
            try {
              await onEdit(task, cleanTitle);
            } catch {
              // The global toast already explains the failure.
            }
          }}
        >
          <input
            autoFocus
            value={editTitle}
            onChange={(event) => onEditTitle(event.target.value)}
            aria-label={`Edit ${task.title}`}
          />
          <button
            className="task-action save"
            disabled={!editTitle.trim()}
            aria-label="Save task"
          >
            <Check size={15} />
          </button>
          <button
            type="button"
            className="task-action"
            onClick={onCancelEditing}
            aria-label="Cancel editing"
          >
            <X size={15} />
          </button>
        </form>
      ) : (
        <>
          <span>{task.title}</span>
          <button
            className="task-action"
            disabled={pending}
            onClick={onStartEditing}
            aria-label={`Edit ${task.title}`}
          >
            <Pencil size={15} />
          </button>
          <button
            className="task-action danger"
            disabled={pending}
            onClick={() => void onDelete(task).catch(() => undefined)}
            aria-label={`Delete ${task.title}`}
          >
            <Trash2 size={15} />
          </button>
        </>
      )}
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  detail,
  tone
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
  tone: string;
}) {
  return (
    <div className={`metric-card ${tone}`}>
      <div className="metric-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </div>
  );
}

function PanelHeader({
  icon,
  title,
  subtitle,
  action
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  action?: ReactNode;
}) {
  return (
    <div className="panel-header">
      <div className="panel-title-icon">{icon}</div>
      <div><h3>{title}</h3><p>{subtitle}</p></div>
      {action && <div className="panel-action">{action}</div>}
    </div>
  );
}

function ProductionView({
  data,
  filter,
  formatFilter,
  search,
  onEdit,
  onMove
}: {
  data: BootstrapData;
  filter: string;
  formatFilter: string;
  search: string;
  onEdit: (video: Video) => void;
  onMove: (video: Video, status: string) => void;
}) {
  const [layout, setLayout] = useState(() => localStorage.getItem("forge-production-layout") || "table");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [filter, formatFilter, search, statusFilter]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  const visible = data.videos.filter((video) => {
    const channel = data.channels.find((item) => item.id === video.channel_id);
    const matchesChannel = filter === "all" || video.channel_id === filter;
    const matchesFormat =
      formatFilter === "all" || effectiveVideoFormat(video, channel) === formatFilter;
    const matchesSearch = video.title.toLowerCase().includes(search.toLowerCase());
    return matchesChannel && matchesFormat && matchesSearch;
  });

  function handleDragEnd(event: DragEndEvent) {
    const video = data.videos.find((item) => item.id === event.active.id);
    const target = String(event.over?.id || "");
    if (!video || !target.startsWith("video-col-")) return;
    const status = target.replace("video-col-", "");
    if (status !== video.status) onMove(video, status);
  }

  const rows = visible.filter(video => statusFilter === "all" || video.status === statusFilter);
  const pageCount = Math.max(1, Math.ceil(rows.length / 50));
  const currentPage = Math.min(page, pageCount - 1);
  return (<>
    <div className="compact-toolbar">
      <strong>{rows.length} bài</strong>
      <select aria-label="Lọc trạng thái" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
        <option value="all">Tất cả trạng thái</option>
        {VIDEO_COLUMNS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <div className="compact-view-switch">{["table", "board"].map(mode => <button key={mode} className={layout === mode ? "active" : ""} onClick={() => { setLayout(mode); localStorage.setItem("forge-production-layout", mode); }}>{mode === "table" ? "Danh sách" : "Bảng thẻ"}</button>)}</div>
    </div>
    {layout === "table" ? <>
      <div className="compact-table-wrap"><table className="compact-table"><thead><tr><th>Bài viết / video</th><th>Kênh</th><th>Trạng thái</th><th>Ưu tiên</th><th>Phụ trách</th><th>Hạn</th></tr></thead><tbody>
        {rows.slice(currentPage * 50, currentPage * 50 + 50).map(video => <tr key={video.id}>
          <td><button className="compact-title" onClick={() => onEdit(video)} title={video.title}>{video.title || "Chưa đặt tên"}</button></td>
          <td>{data.channels.find(c => c.id === video.channel_id)?.name || "—"}</td>
          <td><select aria-label={`Trạng thái: ${video.title}`} value={video.status} onChange={e => onMove(video, e.target.value)}>{VIDEO_COLUMNS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}</select></td>
          <td>{video.priority || "—"}</td><td>{video.owner || "—"}</td><td>{video.due_date ? formatDate(video.due_date) : "—"}</td>
        </tr>)}
        {!rows.length && <tr><td colSpan={6}>Không có bài phù hợp.</td></tr>}
      </tbody></table></div>
      <div className="compact-toolbar"><span>Trang {currentPage + 1}/{pageCount}</span><button disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Trước</button><button disabled={currentPage + 1 >= pageCount} onClick={() => setPage(currentPage + 1)}>Sau</button></div>
    </> : <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="board-scroll">
        <div className="board-grid">
          {VIDEO_COLUMNS.map((column) => {
            const cards = rows.filter((video) => video.status === column.id);
            return (
              <VideoColumn
                key={column.id}
                column={column}
                videos={cards}
                data={data}
                onEdit={onEdit}
              />
            );
          })}
        </div>
      </div>
    </DndContext>}
  </>
  );
}

function VideoColumn({
  column,
  videos,
  data,
  onEdit
}: {
  column: (typeof VIDEO_COLUMNS)[number];
  videos: Video[];
  data: BootstrapData;
  onEdit: (video: Video) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `video-col-${column.id}` });
  return (
    <section ref={setNodeRef} className={`board-column ${isOver ? "is-over" : ""}`}>
      <div className="column-header">
        <div>
          <span className="column-dot" style={{ background: column.color }} />
          <strong>{column.label}</strong>
          <em>{videos.length}</em>
        </div>
        <small>{column.hint}</small>
      </div>
      <div className="column-body">
        {videos.map((video) => (
          <VideoCard
            key={video.id}
            video={video}
            channel={data.channels.find((channel) => channel.id === video.channel_id)}
            assets={data.assets.filter((asset) => asset.video_id === video.id)}
            onEdit={() => onEdit(video)}
          />
        ))}
        {!videos.length && (
          <div className="drop-zone">
            <GripVertical size={16} />
            Drop card here
          </div>
        )}
      </div>
    </section>
  );
}

function VideoCard({
  video,
  channel,
  assets,
  onEdit
}: {
  video: Video;
  channel?: Channel;
  assets: Asset[];
  onEdit: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: video.id
  });
  const style: CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : {};
  const script = assets.some((asset) => asset.type === "script") || Boolean(video.script_text);
  const voice = assets.some((asset) => asset.type === "voiceover");
  const thumbnail = assets.some((asset) => asset.type === "thumbnail");

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`video-card ${isDragging ? "dragging" : ""}`}
      {...listeners}
      {...attributes}
      onClick={onEdit}
    >
      <div className="video-card-top">
        <div className="video-card-badges">
          <span className={`priority-chip ${video.priority}`}>{video.priority}</span>
          <FormatBadge format={effectiveVideoFormat(video, channel)} compact />
        </div>
        <button className="ghost-icon" aria-label="Edit video"><MoreHorizontal size={16} /></button>
      </div>
      <h4>{video.title}</h4>
      <div className="channel-line">
        {channel && <ChannelAvatar channel={channel} small />}
        <span>{channel?.name || "Unknown channel"}</span>
      </div>
      {video.demand_evidence && (
        <p className="evidence-line">
          <BarChart3 size={13} /> {video.demand_evidence}
        </p>
      )}
      <div className="video-card-footer">
        <div className="asset-dots">
          <span className={script ? "done" : ""} title="Script"><FileText size={13} /></span>
          <span className={voice ? "done" : ""} title="Voiceover"><FileAudio size={13} /></span>
          <span className={thumbnail ? "done" : ""} title="Thumbnail"><Image size={13} /></span>
        </div>
        <time title="Published date">
          <CalendarDays size={13} /> {formatDate(video.publish_date)}
        </time>
      </div>
    </article>
  );
}

function ChannelsView({
  data,
  search,
  formatFilter,
  statusFilter,
  onFormatFilter,
  onStatusFilter,
  onEdit,
  onFilterProduction
}: {
  data: BootstrapData;
  search: string;
  formatFilter: string;
  statusFilter: string;
  onFormatFilter: (value: string) => void;
  onStatusFilter: (value: string) => void;
  onEdit: (channel: Channel) => void;
  onFilterProduction: (id: string) => void;
}) {
  const query = search.trim().toLowerCase();
  const filteredChannels = data.channels.filter((channel) => {
    const references = data.channel_references.filter(
      (reference) => reference.channel_id === channel.id
    );
    const topicSources = data.channel_topic_sources.filter(
      (source) => source.channel_id === channel.id
    );
    const matchesSearch = [
      channel.name,
      channel.niche,
      channel.angle,
      channel.host_name,
      channel.reference_channel,
      channel.reference_url,
      CHANNEL_2FA_STATUS[channel.security_2fa_status]?.label || "",
      ...references.flatMap((reference) => [reference.name, reference.url]),
      ...topicSources.flatMap((source) => [
        source.name,
        source.url,
        source.notes
      ])
    ].some((value) => value.toLowerCase().includes(query));
    const matchesFormat =
      formatFilter === "all" || productionFormat(channel.primary_format) === formatFilter;
    const matchesStatus = statusFilter === "all" || channel.status === statusFilter;
    return matchesSearch && matchesFormat && matchesStatus;
  });
  return (
    <>
      <div className="view-filter-bar">
        <FilterSelect
          label="Format"
          value={formatFilter}
          onChange={onFormatFilter}
          options={[
            { id: "all", label: "All formats" },
            ...Object.entries(PRODUCTION_FORMATS).map(([id, item]) => ({ id, label: item.label }))
          ]}
        />
        <FilterSelect
          label="Lifecycle"
          value={statusFilter}
          onChange={onStatusFilter}
          options={[
            { id: "all", label: "All lifecycle states" },
            { id: "idea", label: "Idea" },
            { id: "setup", label: "Setup" },
            { id: "running", label: "Running" },
            { id: "monetized", label: "Monetized" },
            { id: "scaled", label: "Scaled" },
            { id: "paused", label: "Paused" },
            { id: "killed", label: "Killed" }
          ]}
        />
      </div>
      <div className="channel-grid">
      {filteredChannels.map((channel) => {
        const score = readiness(channel);
        const videos = data.videos.filter((video) => video.channel_id === channel.id);
        const posted = videos.filter((video) => video.status === "posted").length;
        const active = videos.filter((video) => !["posted", "archived"].includes(video.status)).length;
        return (
          <article className="channel-card" key={channel.id}>
            <div className="channel-card-glow" style={{ background: channel.color }} />
            <div className="channel-card-head">
              <ChannelAvatar channel={channel} large />
              <div className="channel-card-head-actions">
                <FormatBadge format={productionFormat(channel.primary_format)} />
                <button className="ghost-icon" onClick={() => onEdit(channel)}><Pencil size={15} /></button>
              </div>
            </div>
            <div className="channel-label-row">
              <p className="eyebrow">{channel.niche}</p>
              <span className={`channel-status ${channel.status}`}>{channel.status}</span>
            </div>
            <h3>{channel.name}</h3>
            <span className={`channel-security-status ${channel.security_2fa_status}`}>
              <ShieldCheck size={12} />
              {CHANNEL_2FA_STATUS[channel.security_2fa_status]?.label ||
                CHANNEL_2FA_STATUS.not_checked.label}
            </span>
            <p className="channel-angle">{channel.angle}</p>
            <div className="production-stack-tags">
              {parseStringList(channel.production_stack, [channel.primary_format]).map((item) => (
                <span key={item}>{PRODUCTION_STACK_OPTIONS.find((option) => option.id === item)?.label || item}</span>
              ))}
            </div>
            <div className="channel-stats">
              <div><strong>{score}%</strong><span>Setup</span></div>
              <div><strong>{active}</strong><span>Active</span></div>
              <div><strong>{posted}/{channel.target_videos}</strong><span>Posted</span></div>
            </div>
            <div className="mini-progress">
              <span style={{ width: `${score}%`, background: channel.color }} />
            </div>
            <div className="channel-card-actions">
              <button onClick={() => onEdit(channel)}>Open HQ <ChevronRight size={14} /></button>
              <button onClick={() => onFilterProduction(channel.id)}>Production <KanbanSquare size={14} /></button>
            </div>
          </article>
        );
      })}
      {!filteredChannels.length && <EmptyCompact text="Không tìm thấy channel phù hợp." />}
      </div>
    </>
  );
}

function FormatBadge({
  format,
  compact = false
}: {
  format: ProductionFormatId;
  compact?: boolean;
}) {
  const item = PRODUCTION_FORMATS[format];
  return (
    <span
      className={`format-badge ${compact ? "compact" : ""}`}
      style={{ color: item.color, borderColor: `${item.color}66`, background: `${item.color}16` }}
    >
      {compact ? item.shortLabel : item.label}
    </span>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { id: string; label: string }[];
}) {
  return (
    <label className="filter-select">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.id} value={option.id}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function ChannelAvatar({
  channel,
  small,
  large
}: {
  channel: Channel;
  small?: boolean;
  large?: boolean;
}) {
  return (
    <span
      className={`channel-avatar ${small ? "small" : ""} ${large ? "large" : ""}`}
      style={{ background: `${channel.color}22`, color: channel.color, borderColor: `${channel.color}55` }}
    >
      {channel.profile_image_url ? (
        <img src={channel.profile_image_url} alt="" />
      ) : (
        initials(channel.name)
      )}
    </span>
  );
}

function NichesView({
  niches,
  references,
  obsidianNotes,
  patterns,
  mode,
  onModeChange,
  search,
  formatFilter,
  onFormatFilter,
  onEdit,
  onEditPattern
}: {
  niches: Niche[];
  references: NicheReference[];
  obsidianNotes: NicheObsidianNote[];
  patterns: IdeationPattern[];
  mode: "niches" | "patterns";
  onModeChange: (mode: "niches" | "patterns") => void;
  search: string;
  formatFilter: string;
  onFormatFilter: (value: string) => void;
  onEdit: (niche: Niche) => void;
  onEditPattern: (pattern: IdeationPattern) => void;
}) {
  const query = search.trim().toLowerCase();
  const filteredNiches = niches
    .filter((niche) => {
      const additionalReferences = references.filter(
        (reference) => reference.niche_id === niche.id
      );
      const additionalNotes = obsidianNotes.filter(
        (note) => note.niche_id === niche.id
      );
      const matchesSearch = [
        niche.name,
        niche.niche_angle,
        niche.market,
        niche.audience,
        niche.angle,
        niche.reference_channel,
        niche.reference_url,
        niche.source_note,
        ...additionalReferences.flatMap((reference) => [reference.name, reference.url]),
        ...additionalNotes.flatMap((note) => [note.name, note.note_path])
      ].some((value) => value.toLowerCase().includes(query));
      const formats = nicheFormats(niche);
      const matchesFormat = formatFilter === "all" ||
        (formatFilter === "hybrid" ? formats.length > 1 : formats.includes(formatFilter as ProductionFormatId));
      return matchesSearch && matchesFormat;
    })
    .sort((left, right) => Number(right.launch_asap) - Number(left.launch_asap));
  const filteredPatterns = patterns.filter((pattern) =>
    [
      pattern.name,
      pattern.family,
      pattern.status,
      pattern.title_formula,
      pattern.thumbnail_formula,
      pattern.core_hook,
      pattern.target_niche,
      pattern.source_channel,
      pattern.proof_snapshot,
      pattern.notes
    ].some((value) => value.toLowerCase().includes(query))
  );
  return (
    <div className="niche-layout">
      <div className="lab-tabs" role="tablist" aria-label="Niche Lab sections">
        <button
          type="button"
          className={mode === "niches" ? "active" : ""}
          onClick={() => onModeChange("niches")}
          role="tab"
          aria-selected={mode === "niches"}
        >
          Niche Queue <span>{niches.length}</span>
        </button>
        <button
          type="button"
          className={mode === "patterns" ? "active" : ""}
          onClick={() => onModeChange("patterns")}
          role="tab"
          aria-selected={mode === "patterns"}
        >
          Ideation Pattern Library <span>{patterns.length}</span>
        </button>
      </div>
      {mode === "patterns" ? (
        <div className="pattern-compact-grid">
          {filteredPatterns.map((pattern) => {
            const status = PATTERN_STATUS[pattern.status] || PATTERN_STATUS.captured;
            return (
              <article
                className="pattern-compact-card"
                key={pattern.id}
                onClick={() => onEditPattern(pattern)}
              >
                <div className="pattern-compact-top">
                  <span style={{ color: status.color, background: `${status.color}16` }}>
                    <i style={{ background: status.color }} /> {status.label}
                  </span>
                  <small>{pattern.family || "Pattern"}</small>
                </div>
                <h3>{pattern.name}</h3>
                <dl>
                  <div>
                    <dt>Title</dt>
                    <dd>{pattern.title_formula || "Chưa có title formula"}</dd>
                  </div>
                  <div>
                    <dt>Thumb</dt>
                    <dd>{pattern.thumbnail_formula || "Chưa có thumbnail formula"}</dd>
                  </div>
                </dl>
                <div className="pattern-compact-footer">
                  <span>{pattern.target_niche || "Chưa chọn target niche"}</span>
                  {safeExternalUrl(pattern.source_url) && (
                    <a
                      href={safeExternalUrl(pattern.source_url)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`Open source for ${pattern.name}`}
                    >
                      <ExternalLink size={13} />
                    </a>
                  )}
                </div>
              </article>
            );
          })}
          {!filteredPatterns.length && <EmptyCompact text="Chưa có ideation pattern phù hợp." />}
        </div>
      ) : (
        <>
      <div className="view-filter-bar niche-format-filter">
        <FilterSelect
          label="Planned format"
          value={formatFilter}
          onChange={onFormatFilter}
          options={[
            { id: "all", label: "All formats" },
            ...Object.entries(PRODUCTION_FORMATS).map(([id, item]) => ({ id, label: item.label }))
          ]}
        />
      </div>
      <div className="niche-summary">
        <div>
          <span>Launch queue</span>
          <strong>{niches.filter((niche) => niche.status === "launch_ready").length}</strong>
        </div>
        <div>
          <span>In validation</span>
          <strong>{niches.filter((niche) => niche.status === "validation").length}</strong>
        </div>
        <div>
          <span>Research backlog</span>
          <strong>{niches.filter((niche) => ["idea", "research"].includes(niche.status)).length}</strong>
        </div>
        <p>
          <CircleHelp size={15} />
          Conviction là mức tin tưởng vận hành, không phải market proof.
        </p>
      </div>
      <div className="niche-grid">
        {filteredNiches.map((niche) => {
          const status = NICHE_STATUS[niche.status] || NICHE_STATUS.idea;
          const researchNotes = [
            ...(niche.source_note
              ? [{
                  id: `legacy-note-${niche.id}`,
                  name: obsidianNoteLabel(niche.source_note, 1),
                  note_path: niche.source_note
                }]
              : []),
            ...obsidianNotes
              .filter((note) => note.niche_id === niche.id)
              .sort((left, right) => left.position - right.position)
          ].filter((note) => obsidianNoteUrl(note.note_path));
          const nicheReferences = [
            ...(niche.reference_channel || niche.reference_url
              ? [{
                  id: `primary-${niche.id}`,
                  name: niche.reference_channel || "Reference 1",
                  url: niche.reference_url
                }]
              : []),
            ...references
              .filter((reference) => reference.niche_id === niche.id)
              .map((reference) => ({
                id: reference.id,
                name: reference.name,
                url: reference.url
              }))
          ];
          return (
            <article
              className={`niche-card ${niche.launch_asap ? "launch-asap" : ""}`}
              key={niche.id}
              onClick={() => onEdit(niche)}
            >
              <div className="niche-card-top">
                <span className="status-badge" style={{ color: status.color, background: `${status.color}18` }}>
                  <i style={{ background: status.color }} /> {status.label}
                </span>
                <div className="niche-card-flags">
                  {Boolean(niche.launch_asap) && (
                    <span className="launch-asap-badge">🔥 HOT</span>
                  )}
                  <span className="market-chip">{niche.market}</span>
                </div>
              </div>
              <h3>{niche.name}</h3>
              {niche.niche_angle && <div className="niche-angle-label">{niche.niche_angle}</div>}
              <p>{niche.angle}</p>
              <div className="conviction">
                <div><span>Conviction</span><strong>{niche.conviction}/100</strong></div>
                <div className="mini-progress"><span style={{ width: `${niche.conviction}%`, background: status.color }} /></div>
              </div>
              <div className="niche-meta">
                <div className="format-badge-list">
                  {nicheFormats(niche).map((format) => <FormatBadge key={format} format={format} compact />)}
                </div>
                <span><Users size={14} /> {niche.audience || "Audience TBD"}</span>
              </div>
              {nicheReferences.length > 0 && (
                <div className="niche-reference-list">
                  {nicheReferences.map((reference, index) => (
                    <a
                      key={reference.id}
                      className={!safeExternalUrl(reference.url) ? "disabled" : ""}
                      href={safeExternalUrl(reference.url) || undefined}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`Open ${reference.name} reference`}
                    >
                      <span>REF {index + 1}</span>
                      <strong>{reference.name}</strong>
                      <ExternalLink size={14} />
                    </a>
                  ))}
                </div>
              )}
              {niche.product_path && (
                <div className="product-path">
                  <CircleDollarSign size={14} />
                  {niche.product_path}
                </div>
              )}
              {researchNotes.length > 0 && (
                <div className="niche-obsidian-list">
                  {researchNotes.map((note, index) => (
                    <a
                      key={note.id}
                      className="niche-obsidian-link"
                      href={obsidianNoteUrl(note.note_path)}
                      onClick={(event) => event.stopPropagation()}
                      title={`Open ${note.name || `research note ${index + 1}`} in Obsidian`}
                    >
                      <BookOpen size={14} />
                      <span>{note.name || `Research note ${index + 1}`}</span>
                    </a>
                  ))}
                </div>
              )}
              <button className="text-button">Open thesis <ChevronRight size={14} /></button>
            </article>
          );
        })}
        {!filteredNiches.length && <EmptyCompact text="Không tìm thấy niche phù hợp." />}
      </div>
        </>
      )}
    </div>
  );
}

function ProductsView({
  data,
  onEdit,
  onMove
}: {
  data: BootstrapData;
  onEdit: (product: Product) => void;
  onMove: (product: Product, status: string) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  function handleDragEnd(event: DragEndEvent) {
    const product = data.products.find((item) => item.id === event.active.id);
    const target = String(event.over?.id || "");
    if (!product || !target.startsWith("product-col-")) return;
    const status = target.replace("product-col-", "");
    if (status !== product.status) onMove(product, status);
  }

  return (
    <>
      {!data.products.length && (
        <div className="monetization-intro">
          <div className="intro-icon"><BookOpen size={27} /></div>
          <div>
            <p className="eyebrow">Start after signal</p>
            <h2>Biến channel traction thành owned revenue.</h2>
            <p>
              Sau 7–10 ngày launch, tạo ebook/lead magnet từ những channel có
              signal. Mỗi product card giữ offer, sales page, checkout và revenue
              cùng một chỗ.
            </p>
          </div>
          <div className="funnel-preview">
            <span>YouTube</span><ArrowRight size={15} />
            <span>Lead magnet</span><ArrowRight size={15} />
            <span>Ebook</span><ArrowRight size={15} />
            <span>Email asset</span>
          </div>
        </div>
      )}
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div className="board-scroll product-board">
          <div className="board-grid product-grid">
            {PRODUCT_COLUMNS.map((column) => (
              <ProductColumn
                key={column.id}
                column={column}
                products={data.products.filter((product) => product.status === column.id)}
                channels={data.channels}
                onEdit={onEdit}
              />
            ))}
          </div>
        </div>
      </DndContext>
    </>
  );
}

function ProductColumn({
  column,
  products,
  channels,
  onEdit
}: {
  column: (typeof PRODUCT_COLUMNS)[number];
  products: Product[];
  channels: Channel[];
  onEdit: (product: Product) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `product-col-${column.id}` });
  return (
    <section ref={setNodeRef} className={`board-column ${isOver ? "is-over" : ""}`}>
      <div className="column-header">
        <div>
          <span className="column-dot" style={{ background: column.color }} />
          <strong>{column.label}</strong><em>{products.length}</em>
        </div>
      </div>
      <div className="column-body">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            channel={channels.find((item) => item.id === product.channel_id)}
            onEdit={() => onEdit(product)}
          />
        ))}
        {!products.length && <div className="drop-zone"><GripVertical size={16} /> Drop product here</div>}
      </div>
    </section>
  );
}

function ProductCard({
  product,
  channel,
  onEdit
}: {
  product: Product;
  channel?: Channel;
  onEdit: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: product.id
  });
  const style: CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : {};
  return (
    <article
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={onEdit}
      className={`video-card product-card ${isDragging ? "dragging" : ""}`}
    >
      <div className="video-card-top">
        <span className="type-chip">{product.type}</span>
        <MoreHorizontal size={16} />
      </div>
      <h4>{product.name}</h4>
      <div className="channel-line">
        {channel ? <><ChannelAvatar channel={channel} small /><span>{channel.name}</span></> : <span>Network product</span>}
      </div>
      <div className="product-numbers">
        <div><span>Price</span><strong>{product.price ? `${product.currency} ${product.price}` : "TBD"}</strong></div>
        <div><span>Revenue</span><strong>${Number(product.revenue || 0).toLocaleString()}</strong></div>
      </div>
      <div className="video-card-footer">
        <span><CalendarDays size={13} /> {formatDate(product.due_date)}</span>
        {product.sales_page_url && <ExternalLink size={13} />}
      </div>
    </article>
  );
}

function HeyGenAccountsView({
  accounts,
  search,
  onEdit,
  onDelete,
  onError
}: {
  accounts: HeyGenAccount[];
  search: string;
  onEdit: (account: HeyGenAccount) => void;
  onDelete: (account: HeyGenAccount) => Promise<unknown>;
  onError: (message: string) => void;
}) {
  const query = search.trim().toLowerCase();
  const filtered = accounts.filter((account) => {
    if (!query) return true;
    const status = HEYGEN_ACCOUNT_STATUS[account.status]?.label || account.status;
    return [account.account_number, account.email, account.price, status]
      .some((value) => String(value || "").toLowerCase().includes(query));
  });
  const apiConnected = accounts.filter((account) => account.status === "api_connected").length;
  const failed = accounts.filter((account) => account.status === "failed").length;

  async function copyPassword(account: HeyGenAccount) {
    try {
      await navigator.clipboard.writeText(account.password);
      onError(`Đã copy password account #${account.account_number}`);
      window.setTimeout(() => onError(""), 2200);
    } catch (error) {
      onError(error instanceof Error ? error.message : "Không thể copy password");
    }
  }

  return (
    <div className="heygen-layout">
      <section className="heygen-summary">
        <div className="heygen-summary-icon"><LockKeyhole size={23} /></div>
        <div>
          <p className="eyebrow">Admin-only list</p>
          <h2>{accounts.length} HeyGen accounts</h2>
          <p>Password hiện trực tiếp để copy và dùng nhanh; account hết hạn có thể xoá ngay.</p>
        </div>
        <div className="heygen-summary-stats">
          <span><strong>{apiConnected}</strong> API connected</span>
          <span><strong>{failed}</strong> failed</span>
        </div>
      </section>

      <section className="heygen-table-wrap">
        <div className="heygen-table-head">
          <span>#</span>
          <span>Email / login</span>
          <span>Password</span>
          <span>Price</span>
          <span>Purchased</span>
          <span>Expires</span>
          <span>Status</span>
          <span />
        </div>
        <div className="heygen-table-body">
          {filtered.map((account) => (
            <article className="heygen-row" key={account.id}>
              <strong className="heygen-number">#{account.account_number}</strong>
              <div className="heygen-email" title={account.email}>{account.email}</div>
              <div className="heygen-password">
                <code>{account.password || "Chưa có"}</code>
                {account.password && (
                  <button
                    className="ghost-icon"
                    onClick={() => copyPassword(account)}
                    aria-label="Copy password"
                    title="Copy password"
                  >
                    <Copy size={15} />
                  </button>
                )}
              </div>
              <span className="heygen-price">{account.price || "—"}</span>
              <span>{formatDate(account.purchased_on)}</span>
              <span>{formatDate(account.expires_on)}</span>
              <span className={`heygen-status ${account.status}`}>
                <i /> {HEYGEN_ACCOUNT_STATUS[account.status]?.label || account.status}
              </span>
              <div className="heygen-row-actions">
                <button className="ghost-icon" onClick={() => onEdit(account)} aria-label="Edit account">
                  <Pencil size={15} />
                </button>
                <button
                  className="ghost-icon danger"
                  onClick={() => {
                    if (window.confirm(`Xóa HeyGen account #${account.account_number}?`)) {
                      void onDelete(account);
                    }
                  }}
                  aria-label="Delete account"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </article>
          ))}
          {!filtered.length && (
            <div className="empty-compact">
              {accounts.length ? "Không tìm thấy account phù hợp." : "Chưa có HeyGen account. Bấm Add account để bắt đầu."}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function UsersView({
  users,
  currentUserId,
  onEdit
}: {
  users: User[];
  currentUserId: string;
  onEdit: (user: User) => void;
}) {
  const active = users.filter((user) => user.status === "active").length;
  const admins = users.filter(
    (user) => user.role === "admin" && user.status === "active"
  ).length;

  return (
    <div className="users-layout">
      <section className="users-summary">
        <div className="users-summary-icon"><ShieldCheck size={23} /></div>
        <div>
          <p className="eyebrow">Workspace access</p>
          <h2>{active} active users</h2>
          <p>
            Admin tạo account, reset password hoặc vô hiệu hóa quyền truy cập.
            Member vẫn làm được channel, video và product nhưng không quản lý user.
          </p>
        </div>
        <span>{admins} admin{admins === 1 ? "" : "s"}</span>
      </section>

      <div className="users-grid">
        {users.map((user) => (
          <article className={`user-card ${user.status}`} key={user.id}>
            <div className="user-avatar">{initials(user.display_name)}</div>
            <div className="user-card-copy">
              <div>
                <h3>{user.display_name}</h3>
                {user.id === currentUserId && <span className="you-chip">You</span>}
              </div>
              <p>@{user.username}</p>
            </div>
            <button
              className="ghost-icon"
              onClick={() => onEdit(user)}
              aria-label={`Edit ${user.display_name}`}
            >
              <Pencil size={15} />
            </button>
            <div className="user-card-meta">
              <span className={`role-chip ${user.role}`}>
                {user.role === "admin" ? <ShieldCheck size={13} /> : <Users size={13} />}
                {user.role}
              </span>
              <span className={`account-status ${user.status}`}>
                <i /> {user.status}
              </span>
            </div>
            <div className="user-last-login">
              <span>Last login</span>
              <strong>
                {user.last_login_at
                  ? new Intl.DateTimeFormat("vi-VN", {
                      dateStyle: "medium",
                      timeStyle: "short"
                    }).format(new Date(user.last_login_at))
                  : "Chưa đăng nhập"}
              </strong>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function Modal({
  title,
  eyebrow,
  children,
  onClose,
  footer,
  wide = false
}: {
  title: string;
  eyebrow: string;
  children: ReactNode;
  onClose: () => void;
  footer: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={`modal ${wide ? "wide" : ""}`} role="dialog" aria-modal="true">
        <header>
          <div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></div>
          <button className="icon-button" onClick={onClose}><X size={18} /></button>
        </header>
        <div className="modal-body">{children}</div>
        <footer>{footer}</footer>
      </section>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
  span = false
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  span?: boolean;
}) {
  return (
    <label className={`field ${span ? "span-2" : ""}`}>
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function ModalActions({
  onClose,
  onDelete,
  submitLabel = "Save changes",
  formId
}: {
  onClose: () => void;
  onDelete?: () => void;
  submitLabel?: string;
  formId: string;
}) {
  return (
    <>
      {onDelete ? (
        <button type="button" className="danger-button" onClick={onDelete}>
          <Trash2 size={15} /> Delete
        </button>
      ) : <span />}
      <div>
        <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
        <button type="submit" form={formId} className="primary-button"><Check size={15} /> {submitLabel}</button>
      </div>
    </>
  );
}

function HeyGenAccountModal({
  value,
  onClose,
  onSave,
  onDelete
}: {
  value: Partial<HeyGenAccount> & { password?: string };
  onClose: () => void;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
  onDelete?: () => void;
}) {
  const formId = "heygen-account-form";
  return (
    <Modal
      title={value.id ? `Edit account #${value.account_number}` : "Add HeyGen account"}
      eyebrow="HeyGen Account Manager"
      onClose={onClose}
      footer={
        <ModalActions
          formId={formId}
          onClose={onClose}
          onDelete={onDelete}
          submitLabel={value.id ? "Save account" : "Add account"}
        />
      }
    >
      <form
        id={formId}
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const payload = formValues(event.currentTarget);
          if (!payload.account_number) delete payload.account_number;
          onSave(payload);
        }}
      >
        <Field label="Số thứ tự" hint={value.id ? "Có thể đổi nếu cần." : "Để trống để app tự tăng số."}>
          <input
            type="number"
            name="account_number"
            min={1}
            step={1}
            defaultValue={value.account_number || ""}
          />
        </Field>
        <Field label="Trạng thái">
          <select name="status" defaultValue={value.status || "login_success"}>
            <option value="login_success">Log thành công</option>
            <option value="api_connected">Connected API</option>
            <option value="failed">Fail</option>
          </select>
        </Field>
        <Field label="Email / login" span>
          <input
            name="email"
            defaultValue={value.email || ""}
            required
            autoFocus
            autoCapitalize="none"
            autoComplete="off"
          />
        </Field>
        <Field label="Password" hint="Hiện trực tiếp trong bảng để dùng nhanh." span>
          <input
            type="text"
            name="password"
            defaultValue={value.password || ""}
            autoComplete="off"
          />
        </Field>
        <Field label="Giá">
          <input name="price" defaultValue={value.price || ""} placeholder="$3/acc hoặc 190.000đ" />
        </Field>
        <span />
        <Field label="Ngày mua">
          <input type="date" name="purchased_on" defaultValue={value.purchased_on || ""} />
        </Field>
        <Field label="Ngày hết hạn">
          <input type="date" name="expires_on" defaultValue={value.expires_on || ""} />
        </Field>
        <div className="account-help span-2">
          <ShieldCheck size={17} />
          <p>Chỉ Admin thấy và quản lý danh sách HeyGen account này.</p>
        </div>
      </form>
    </Modal>
  );
}

function UserModal({
  value,
  currentUserId,
  onClose,
  onSave
}: {
  value: Partial<User> & { password?: string };
  currentUserId: string;
  onClose: () => void;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const formId = "user-form";
  const isSelf = value.id === currentUserId;
  return (
    <Modal
      title={value.id ? `Edit ${value.display_name}` : "Create collaborator account"}
      eyebrow="User Management"
      onClose={onClose}
      footer={
        <ModalActions
          formId={formId}
          onClose={onClose}
          submitLabel={value.id ? "Save account" : "Create user"}
        />
      }
    >
      <form
        id={formId}
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const payload = formValues(event.currentTarget);
          if (!payload.password) delete payload.password;
          onSave(payload);
        }}
      >
        <Field label="Display name" span>
          <input
            name="display_name"
            defaultValue={value.display_name || ""}
            required
            autoFocus
            autoComplete="off"
          />
        </Field>
        <Field label="Username">
          <input
            name="username"
            defaultValue={value.username || ""}
            required
            minLength={3}
            autoCapitalize="none"
            autoComplete="off"
          />
        </Field>
        <Field label="Role">
          <select name="role" defaultValue={value.role || "member"} disabled={isSelf}>
            <option value="member">Member — work access</option>
            <option value="admin">Admin — full access</option>
          </select>
          {isSelf && <input type="hidden" name="role" value="admin" />}
        </Field>
        {value.id && (
          <Field label="Account status">
            <select
              name="status"
              defaultValue={value.status || "active"}
              disabled={isSelf}
            >
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
            {isSelf && <input type="hidden" name="status" value="active" />}
          </Field>
        )}
        <Field
          label={value.id ? "New password" : "Temporary password"}
          hint={value.id ? "Để trống nếu không muốn reset. Tối thiểu 10 ký tự." : "Tối thiểu 10 ký tự. Gửi riêng cho người dùng."}
          span
        >
          <input
            type="password"
            name="password"
            minLength={10}
            required={!value.id}
            autoComplete="new-password"
          />
        </Field>
        {!value.id && (
          <div className="account-help span-2">
            <UserPlus size={17} />
            <p>
              Tạo role <strong>Member</strong> cho người phụ việc. Chỉ cấp Admin
              khi họ cần tạo hoặc khóa account khác.
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
}

function VideoModal({
  value,
  data,
  onClose,
  onSave,
  onDelete,
  onDeleteAsset
}: {
  value: Partial<Video>;
  data: BootstrapData;
  onClose: () => void;
  onSave: (
    payload: Record<string, unknown>,
    pendingAssets: PendingAsset[]
  ) => Promise<void>;
  onDelete?: () => void;
  onDeleteAsset: (asset: Asset) => Promise<void>;
}) {
  const [assetType, setAssetType] = useState("script");
  const [pendingAssets, setPendingAssets] = useState<PendingAsset[]>([]);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const [description, setDescription] = useState(value.description || "");
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState(
    value.channel_id || data.channels[0]?.id || ""
  );
  const [formatOverride, setFormatOverride] = useState(value.format_override || "");
  const [workflowChecklist, setWorkflowChecklist] = useState(
    parseChecklist(value.workflow_checklist)
  );
  const assets = data.assets.filter((asset) => asset.video_id === value.id);
  const formId = "video-form";
  const selectedChannel = data.channels.find((channel) => channel.id === selectedChannelId);
  const inheritedFormat = productionFormat(selectedChannel?.primary_format || "avatarforge");
  const activeFormat = productionFormat(formatOverride || inheritedFormat);
  const workflowSteps = WORKFLOW_TEMPLATES[activeFormat];
  const completedSteps = workflowSteps.filter((step) => workflowChecklist[step.id]).length;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = formValues(event.currentTarget);
    payload.format_override = formatOverride;
    payload.workflow_checklist = JSON.stringify(workflowChecklist);
    await onSave(payload, pendingAssets);
  }

  function queueFiles(files: FileList | File[]) {
    const nextFiles = Array.from(files);
    if (!nextFiles.length) return;
    setPendingAssets((current) => [
      ...current,
      ...nextFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        type: assetType
      }))
    ]);
  }

  function handleFileDrop(event: ReactDragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsFileDragging(false);
    queueFiles(event.dataTransfer.files);
  }

  return (
    <Modal
      title={value.id ? value.title || "Edit video" : "Queue a new video"}
      eyebrow="Production card"
      onClose={onClose}
      wide
      footer={<ModalActions formId={formId} onClose={onClose} onDelete={onDelete} submitLabel={value.id ? "Save video" : "Add to board"} />}
    >
      <form id={formId} className="form-grid" onSubmit={submit}>
        <Field label="Video title" span>
          <input name="title" defaultValue={value.title || ""} placeholder="Title / working title" required autoFocus />
        </Field>
        <Field label="Channel">
          <select
            name="channel_id"
            value={selectedChannelId}
            onChange={(event) => setSelectedChannelId(event.target.value)}
            required
          >
            {data.channels.map((channel) => <option key={channel.id} value={channel.id}>{channel.name}</option>)}
          </select>
        </Field>
        <Field label="Production format" hint={`Inherited: ${PRODUCTION_FORMATS[inheritedFormat].label}`}>
          <select value={formatOverride} onChange={(event) => setFormatOverride(event.target.value)}>
            <option value="">Inherit from channel</option>
            {Object.entries(PRODUCTION_FORMATS).map(([id, item]) => (
              <option key={id} value={id}>{item.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Stage">
          <select name="status" defaultValue={value.status || "new"}>
            {VIDEO_COLUMNS.map((column) => <option key={column.id} value={column.id}>{column.label}</option>)}
          </select>
        </Field>
        <Field label="Priority">
          <select name="priority" defaultValue={value.priority || "normal"}>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </Field>
        <Field label="Owner">
          <input name="owner" defaultValue={value.owner || "Anh"} />
        </Field>
        <Field label="Due date">
          <input type="date" name="due_date" defaultValue={value.due_date || ""} />
        </Field>
        <Field label="Publish date">
          <input type="date" name="publish_date" defaultValue={value.publish_date || ""} />
        </Field>

        <div className="form-section span-2">
          <div><ListChecks size={16} /><h3>{PRODUCTION_FORMATS[activeFormat].label} workflow</h3></div>
          <span>{completedSteps}/{workflowSteps.length} completed</span>
        </div>
        <div className="workflow-checklist span-2">
          {workflowSteps.map((step) => (
            <label className="workflow-step" key={step.id}>
              <input
                type="checkbox"
                checked={Boolean(workflowChecklist[step.id])}
                onChange={(event) => setWorkflowChecklist((current) => ({
                  ...current,
                  [step.id]: event.target.checked
                }))}
              />
              <span>{step.label}</span>
            </label>
          ))}
        </div>

        <div className="form-section span-2">
          <div><BarChart3 size={16} /><h3>Demand proof</h3></div>
        </div>
        <Field label="Topic source" hint="Reference video/channel, feed discovery, search..." span>
          <input name="topic_source" defaultValue={value.topic_source || ""} placeholder="https://youtube.com/... or source note" />
        </Field>
        <Field label="Demand evidence" hint="Ghi snapshot, ngày capture và giới hạn của evidence." span>
          <textarea name="demand_evidence" defaultValue={value.demand_evidence || ""} rows={3} placeholder="Views, outlier signal, title grammar, audience pain..." />
        </Field>

        <div className="form-section span-2">
          <div><FileText size={16} /><h3>Production package</h3></div>
        </div>
        <Field label="Script / outline" span>
          <textarea name="script_text" defaultValue={value.script_text || ""} rows={7} placeholder="Paste script, outline or working notes..." />
        </Field>
        <label className="field expandable-field span-2">
          <span className="expandable-field-header">
            <span>Video description</span>
            <button
              type="button"
              className="editor-expand-button"
              onClick={() => setIsDescriptionExpanded(true)}
              aria-label="Expand video description editor"
              title="Expand editor"
            >
              <Maximize2 size={14} /> Expand
            </button>
          </span>
          <textarea
            name="description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            placeholder="Paste the full YouTube description here..."
          />
        </label>
        <Field label="Thumbnail prompt" span>
          <textarea name="thumbnail_prompt" defaultValue={value.thumbnail_prompt || ""} rows={3} />
        </Field>
        <Field label="YouTube URL">
          <input type="url" name="youtube_url" defaultValue={value.youtube_url || ""} placeholder="After publish" />
        </Field>
        <Field label="Notes">
          <input name="notes" defaultValue={value.notes || ""} />
        </Field>

        <div className="form-section span-2">
          <div><Paperclip size={16} /><h3>Files & assets</h3></div>
          <span>Thả nhiều file rồi lưu một lần</span>
        </div>
        <div className="upload-row span-2">
          <select value={assetType} onChange={(event) => setAssetType(event.target.value)}>
            {ASSET_TYPE_OPTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <label
            className={`file-picker file-drop-picker${isFileDragging ? " dragging" : ""}`}
            onDragEnter={(event) => {
              event.preventDefault();
              setIsFileDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsFileDragging(false);
            }}
            onDrop={handleFileDrop}
          >
            <Upload size={16} />
            <span>Drop files here or choose files</span>
            <input
              type="file"
              multiple
              onChange={(event) => {
                if (event.target.files) queueFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </label>
          <small>{pendingAssets.length ? `${pendingAssets.length} ready` : "Multiple files"}</small>
        </div>
        {pendingAssets.length > 0 && (
          <div className="pending-asset-list span-2">
            <div className="pending-assets-head">
              <strong>Ready to attach ({pendingAssets.length})</strong>
              <small>Files upload when you save this video.</small>
            </div>
            {pendingAssets.map((asset) => (
              <div className="pending-asset" key={asset.id}>
                <span className="asset-icon">
                  {asset.type === "voiceover" ? <FileAudio size={15} /> : asset.type === "thumbnail" ? <Image size={15} /> : <FileText size={15} />}
                </span>
                <span className="pending-asset-name" title={asset.file.name}>{asset.file.name}</span>
                <select
                  value={asset.type}
                  aria-label={`Asset type for ${asset.file.name}`}
                  onChange={(event) => setPendingAssets((current) => current.map((item) => (
                    item.id === asset.id ? { ...item, type: event.target.value } : item
                  )))}
                >
                  {ASSET_TYPE_OPTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
                <small>{formatBytes(asset.file.size)}</small>
                <button
                  type="button"
                  className="ghost-icon"
                  onClick={() => setPendingAssets((current) => current.filter((item) => item.id !== asset.id))}
                  aria-label={`Remove ${asset.file.name}`}
                  title={`Remove ${asset.file.name}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        {assets.length > 0 && (
          <div className="asset-list span-2">
            {assets.map((asset) => (
              <div key={asset.id}>
                <span className="asset-icon">
                  {asset.type === "voiceover" ? <FileAudio size={15} /> : asset.type === "thumbnail" ? <Image size={15} /> : <FileText size={15} />}
                </span>
                <a
                  className="asset-download-link"
                  href={`/api/assets/${asset.id}/download`}
                  download={asset.name}
                  title={`Download ${asset.name}`}
                >
                  <span>{asset.name}</span>
                  <Download size={14} aria-hidden="true" />
                </a>
                <small>{asset.type} · {formatBytes(asset.size_bytes)}</small>
                <button
                  type="button"
                  className="ghost-icon"
                  onClick={() => onDeleteAsset(asset)}
                  aria-label={`Delete ${asset.name}`}
                  title={`Delete ${asset.name}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        {isDescriptionExpanded && (
          <div
            className="expanded-editor-backdrop"
            role="dialog"
            aria-modal="true"
            aria-label="Video description editor"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsDescriptionExpanded(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                setIsDescriptionExpanded(false);
              }
            }}
          >
            <section className="expanded-editor-panel">
              <header>
                <div>
                  <p className="eyebrow">Production card</p>
                  <h3>Video description</h3>
                </div>
                <button
                  type="button"
                  className="editor-collapse-button"
                  onClick={() => setIsDescriptionExpanded(false)}
                  aria-label="Collapse video description editor"
                  title="Collapse editor"
                >
                  <Minimize2 size={16} /> Done
                </button>
              </header>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Paste the full YouTube description here..."
                autoFocus
              />
              <footer>
                <span>{description.length.toLocaleString()} characters</span>
                <span>Esc to collapse</span>
              </footer>
            </section>
          </div>
        )}
      </form>
    </Modal>
  );
}

function ChannelModal({
  value,
  references,
  topicSources,
  onClose,
  onSave,
  onDelete
}: {
  value: Partial<Channel>;
  references: ChannelReference[];
  topicSources: ChannelTopicSource[];
  onClose: () => void;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
  onDelete?: () => void;
}) {
  const formId = "channel-form";
  const initialPrimaryFormat = productionFormat(value.primary_format || "avatarforge");
  const [primaryFormat, setPrimaryFormat] = useState<ProductionFormatId>(initialPrimaryFormat);
  const [productionStack, setProductionStack] = useState(
    parseStringList(value.production_stack, [initialPrimaryFormat])
  );
  const [workflowTemplate, setWorkflowTemplate] = useState<ProductionFormatId>(
    productionFormat(value.workflow_template || initialPrimaryFormat)
  );
  const [primaryReferenceUrl, setPrimaryReferenceUrl] = useState(
    value.reference_url || ""
  );
  const [additionalReferences, setAdditionalReferences] = useState(
    references
      .sort((left, right) => left.position - right.position)
      .map((reference) => ({ name: reference.name, url: reference.url }))
  );
  const [channelTopicSources, setChannelTopicSources] = useState(
    [...topicSources]
      .sort((left, right) => left.position - right.position)
      .map((source) => ({
        name: source.name,
        url: source.url,
        notes: source.notes
      }))
  );
  return (
    <Modal
      title={value.id ? value.name || "Channel HQ" : "Create channel HQ"}
      eyebrow="Channel workspace"
      onClose={onClose}
      wide
      footer={<ModalActions formId={formId} onClose={onClose} onDelete={onDelete} submitLabel={value.id ? "Save channel" : "Create channel"} />}
    >
      <form
        id={formId}
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const payload = formValues(event.currentTarget);
          payload.target_videos = Number(payload.target_videos || 20);
          payload.target_uploads_per_week = Number(payload.target_uploads_per_week || 5);
          payload.primary_format = primaryFormat;
          payload.production_stack = JSON.stringify(productionStack);
          payload.workflow_template = workflowTemplate;
          payload.references = additionalReferences;
          payload.topic_sources = channelTopicSources;
          onSave(payload);
        }}
      >
        <div className="form-section span-2">
          <div><Rocket size={16} /><h3>Identity & launch</h3></div>
          {value.id && <span>{readiness(value as Channel)}% ready</span>}
        </div>
        <Field label="Channel name">
          <input name="name" defaultValue={value.name || ""} required autoFocus />
        </Field>
        <Field label="Status">
          <select name="status" defaultValue={value.status || "setup"}>
            <option value="idea">Idea</option>
            <option value="setup">Setup</option>
            <option value="running">Running</option>
            <option value="monetized">Monetized</option>
            <option value="paused">Paused</option>
            <option value="killed">Killed</option>
            <option value="scaled">Scaled</option>
          </select>
        </Field>
        <Field label="Primary production format">
          <select
            value={primaryFormat}
            onChange={(event) => {
              const next = productionFormat(event.target.value);
              setPrimaryFormat(next);
              setProductionStack((current) => current.includes(next) ? current : [next, ...current]);
              setWorkflowTemplate(next);
            }}
          >
            {Object.entries(PRODUCTION_FORMATS).map(([id, item]) => (
              <option key={id} value={id}>{item.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Workflow template">
          <select
            value={workflowTemplate}
            onChange={(event) => setWorkflowTemplate(productionFormat(event.target.value))}
          >
            {Object.entries(PRODUCTION_FORMATS).map(([id, item]) => (
              <option key={id} value={id}>{item.label}</option>
            ))}
          </select>
        </Field>
        <div className="multi-select-field span-2">
          <strong>Production stack</strong>
          <small>Chọn app/công cụ chính dùng để sản xuất channel này.</small>
          <div className="multi-select-chips">
            {PRODUCTION_STACK_OPTIONS.map((option) => {
              const checked = productionStack.includes(option.id);
              return (
                <label className={checked ? "selected" : ""} key={option.id}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => setProductionStack((current) => {
                      if (event.target.checked) return [...new Set([...current, option.id])];
                      const next = current.filter((item) => item !== option.id);
                      return next.length ? next : [primaryFormat];
                    })}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })}
          </div>
        </div>
        <Field label="Niche">
          <input name="niche" defaultValue={value.niche || ""} required />
        </Field>
        <Field label="Host name">
          <input name="host_name" defaultValue={value.host_name || ""} />
        </Field>
        <Field label="Angle / promise" span>
          <input name="angle" defaultValue={value.angle || ""} />
        </Field>
        <Field label="Launch date">
          <input type="date" name="launch_date" defaultValue={value.launch_date || ""} />
        </Field>
        <Field label="Owner">
          <input name="owner" defaultValue={value.owner || "Anh"} />
        </Field>
        <Field label="Monthly video target">
          <input type="number" name="target_videos" min="1" defaultValue={value.target_videos || 20} />
        </Field>
        <Field label="Uploads / week">
          <input type="number" name="target_uploads_per_week" min="1" defaultValue={value.target_uploads_per_week || 5} />
        </Field>
        <Field
          label="TS impression"
          hint="Ghi impression nội bộ theo cách anh muốn."
        >
          <input
            type="text"
            name="trust_score_impression"
            defaultValue={value.trust_score_impression || ""}
            placeholder="Ví dụ: High trust với audience 60+"
          />
        </Field>
        <Field
          label="2FA / recovery status"
          hint="Chỉ lưu trạng thái; không nhập số điện thoại, mã 2FA hoặc recovery code."
        >
          <select
            name="security_2fa_status"
            defaultValue={value.security_2fa_status || "not_checked"}
          >
            {Object.entries(CHANNEL_2FA_STATUS).map(([id, item]) => (
              <option key={id} value={id}>{item.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Channel color">
          <input type="color" name="color" defaultValue={value.color || "#f59e0b"} />
        </Field>
        <Field label="YouTube URL">
          <input type="url" name="youtube_url" defaultValue={value.youtube_url || ""} />
        </Field>
        <Field label="Channel description" span>
          <textarea name="description" rows={7} defaultValue={value.description || ""} />
        </Field>

        <div className="form-section span-2">
          <div><Link2 size={16} /><h3>Reference & operations</h3></div>
        </div>
        <Field label="Proven reference">
          <input name="reference_channel" defaultValue={value.reference_channel || ""} />
        </Field>
        <Field label="Reference URL">
          <div className="url-field-row">
            <input
              type="url"
              name="reference_url"
              value={primaryReferenceUrl}
              onChange={(event) => setPrimaryReferenceUrl(event.target.value)}
            />
            {safeExternalUrl(primaryReferenceUrl) ? (
              <a
                className="url-open-button"
                href={safeExternalUrl(primaryReferenceUrl)}
                target="_blank"
                rel="noreferrer"
                aria-label="Open primary reference URL"
                title="Open reference URL"
              >
                <ExternalLink size={15} />
              </a>
            ) : (
              <span className="url-open-button disabled" aria-hidden="true">
                <ExternalLink size={15} />
              </span>
            )}
          </div>
        </Field>
        {additionalReferences.map((reference, index) => (
          <div className="reference-pair span-2" key={index}>
            <label>
              <span>Reference channel {index + 2}</span>
              <input
                value={reference.name}
                onChange={(event) =>
                  setAdditionalReferences((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, name: event.target.value } : item
                    )
                  )
                }
                placeholder={`Reference channel ${index + 2}`}
              />
            </label>
            <label>
              <span>Channel URL {index + 2}</span>
              <div className="url-field-row">
                <input
                  type="url"
                  value={reference.url}
                  onChange={(event) =>
                    setAdditionalReferences((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, url: event.target.value } : item
                      )
                    )
                  }
                  placeholder="https://youtube.com/@channel"
                />
                {safeExternalUrl(reference.url) ? (
                  <a
                    className="url-open-button"
                    href={safeExternalUrl(reference.url)}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open reference URL ${index + 2}`}
                    title={`Open reference URL ${index + 2}`}
                  >
                    <ExternalLink size={15} />
                  </a>
                ) : (
                  <span className="url-open-button disabled" aria-hidden="true">
                    <ExternalLink size={15} />
                  </span>
                )}
              </div>
            </label>
            <button
              type="button"
              className="ghost-icon"
              onClick={() =>
                setAdditionalReferences((current) =>
                  current.filter((_, itemIndex) => itemIndex !== index)
                )
              }
              aria-label={`Remove reference ${index + 2}`}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="add-reference-button span-2"
          onClick={() =>
            setAdditionalReferences((current) => [
              ...current,
              { name: "", url: "" }
            ])
          }
        >
          <Plus size={15} />
          Add reference channel {additionalReferences.length + 2}
        </button>

        <div className="form-section span-2">
          <div><BookOpen size={16} /><h3>Topic bank sources</h3></div>
          <span>Nguồn để cào topic/angle, tách khỏi proof channel chính.</span>
        </div>
        {channelTopicSources.map((source, index) => (
          <div className="topic-source-editor span-2" key={index}>
            <div className="topic-source-editor-head">
              <strong>Source {index + 1}</strong>
              <button
                type="button"
                className="ghost-icon"
                onClick={() =>
                  setChannelTopicSources((current) =>
                    current.filter((_, itemIndex) => itemIndex !== index)
                  )
                }
                aria-label={`Remove topic source ${index + 1}`}
              >
                <Trash2 size={15} />
              </button>
            </div>
            <div className="topic-source-editor-grid">
              <label>
                <span>Source name</span>
                <input
                  value={source.name}
                  onChange={(event) =>
                    setChannelTopicSources((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index
                          ? { ...item, name: event.target.value }
                          : item
                      )
                    )
                  }
                  placeholder="Source channel or idea bank"
                />
              </label>
              <label>
                <span>Channel / source URL</span>
                <div className="url-field-row">
                  <input
                    type="url"
                    value={source.url}
                    onChange={(event) =>
                      setChannelTopicSources((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, url: event.target.value }
                            : item
                        )
                      )
                    }
                    placeholder="https://youtube.com/@channel/videos"
                  />
                  {safeExternalUrl(source.url) ? (
                    <a
                      className="url-open-button"
                      href={safeExternalUrl(source.url)}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open topic source ${index + 1}`}
                      title="Open topic source"
                    >
                      <ExternalLink size={15} />
                    </a>
                  ) : (
                    <span className="url-open-button disabled" aria-hidden="true">
                      <ExternalLink size={15} />
                    </span>
                  )}
                </div>
              </label>
            </div>
            <label>
              <span>Notes / what to mine</span>
              <textarea
                rows={3}
                value={source.notes}
                onChange={(event) =>
                  setChannelTopicSources((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index
                        ? { ...item, notes: event.target.value }
                        : item
                    )
                  )
                }
                placeholder="Topic angles, playlists, hooks hoặc lý do cần quay lại cào source này..."
              />
            </label>
          </div>
        ))}
        <button
          type="button"
          className="add-reference-button span-2"
          onClick={() =>
            setChannelTopicSources((current) => [
              ...current,
              { name: "", url: "", notes: "" }
            ])
          }
        >
          <Plus size={15} />
          Add topic bank source
        </button>

        <Field label="Gmail label" hint="Không cần lưu password ở đây.">
          <input name="gmail" defaultValue={value.gmail || ""} />
        </Field>
        <Field label="AdsPower profile">
          <input name="adspower_profile" defaultValue={value.adspower_profile || ""} />
        </Field>
        <Field label="Proxy label" hint="Chỉ lưu label/pool, không lưu credential.">
          <input name="proxy_label" defaultValue={value.proxy_label || ""} />
        </Field>
        <Field label="Profile image URL">
          <input name="profile_image_url" defaultValue={value.profile_image_url || ""} />
        </Field>
        <Field label="Banner URL" span>
          <input name="banner_url" defaultValue={value.banner_url || ""} />
        </Field>

        <div className="form-section span-2">
          <div><Sparkles size={16} /><h3>Avatar & voice</h3></div>
        </div>
        <Field label="HeyGen account">
          <input name="heygen_account" defaultValue={value.heygen_account || ""} />
        </Field>
        <Field label="HeyGen Avatar ID">
          <input name="heygen_avatar_id" defaultValue={value.heygen_avatar_id || ""} />
        </Field>
        <Field label="Avatar prompt" span>
          <textarea name="avatar_prompt" rows={4} defaultValue={value.avatar_prompt || ""} />
        </Field>
        <Field label="Reference image / ID">
          <input name="avatar_reference" defaultValue={value.avatar_reference || ""} />
        </Field>
        <Field label="Fixed wardrobe">
          <input name="avatar_wardrobe" defaultValue={value.avatar_wardrobe || ""} />
        </Field>
        <Field label="Fixed look / background" span>
          <input name="avatar_background" defaultValue={value.avatar_background || ""} />
        </Field>
        <Field label="Voice name">
          <input name="voice_name" defaultValue={value.voice_name || ""} />
        </Field>
        <Field label="Voice ID">
          <input name="voice_id" defaultValue={value.voice_id || ""} />
        </Field>

        <div className="form-section span-2">
          <div><Image size={16} /><h3>Packaging system</h3></div>
        </div>
        <Field label="Thumbnail reference" span>
          <input name="thumbnail_reference" defaultValue={value.thumbnail_reference || ""} />
        </Field>
        <Field label="Thumbnail prompt" span>
          <textarea name="thumbnail_prompt" rows={4} defaultValue={value.thumbnail_prompt || ""} />
        </Field>
        <Field label="Font / color / layout rules" span>
          <textarea name="thumbnail_rules" rows={3} defaultValue={value.thumbnail_rules || ""} />
        </Field>
        <Field label="Title engine" span>
          <textarea name="title_engine" rows={3} defaultValue={value.title_engine || ""} />
        </Field>

      </form>
    </Modal>
  );
}

function NicheModal({
  value,
  references,
  obsidianNotes,
  onClose,
  onSave,
  onDelete
}: {
  value: Partial<Niche>;
  references: NicheReference[];
  obsidianNotes: NicheObsidianNote[];
  onClose: () => void;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
  onDelete?: () => void;
}) {
  const formId = "niche-form";
  const initialPlannedFormats = (() => {
    const parsed = parseStringList(value.planned_formats).map(productionFormat);
    if (parsed.length) return parsed;
    const legacy = String(value.format || "").toLowerCase();
    if (legacy.includes("vidforge")) return ["vidforge" as ProductionFormatId];
    if (legacy.includes("sketch")) return ["sketch" as ProductionFormatId];
    return ["avatarforge" as ProductionFormatId];
  })();
  const [plannedFormats, setPlannedFormats] = useState<ProductionFormatId[]>(initialPlannedFormats);
  const [primaryReferenceUrl, setPrimaryReferenceUrl] = useState(
    value.reference_url || ""
  );
  const [researchNotes, setResearchNotes] = useState(() => {
    const existing = [
      ...(value.source_note
        ? [{ name: obsidianNoteLabel(value.source_note, 1), note_path: value.source_note }]
        : []),
      ...[...obsidianNotes]
        .sort((left, right) => left.position - right.position)
        .map((note) => ({ name: note.name, note_path: note.note_path }))
    ];
    return existing.length ? existing : [{ name: "", note_path: "" }];
  });
  const [additionalReferences, setAdditionalReferences] = useState(
    [...references]
      .sort((left, right) => left.position - right.position)
      .map((reference) => ({ name: reference.name, url: reference.url }))
  );
  return (
    <Modal
      title={value.id ? value.name || "Niche thesis" : "Capture niche idea"}
      eyebrow="Niche Lab"
      onClose={onClose}
      wide
      footer={<ModalActions formId={formId} onClose={onClose} onDelete={onDelete} submitLabel={value.id ? "Save thesis" : "Add niche"} />}
    >
      <form
        id={formId}
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const payload = formValues(event.currentTarget);
          payload.conviction = Number(payload.conviction || 50);
          payload.launch_asap = new FormData(event.currentTarget).has("launch_asap") ? 1 : 0;
          payload.planned_formats = JSON.stringify(plannedFormats);
          payload.format = plannedFormats.map((format) => PRODUCTION_FORMATS[format].label).join(" + ");
          payload.references = additionalReferences;
          payload.source_note = "";
          payload.obsidian_notes = researchNotes;
          onSave(payload);
        }}
      >
        <Field label="Niche / concept" span>
          <input name="name" defaultValue={value.name || ""} required autoFocus />
        </Field>
        <Field label="Niche angle / vertical" span>
          <input
            name="niche_angle"
            defaultValue={value.niche_angle || ""}
            placeholder="Example: Zero-Turn Mower Buyer’s Guide"
          />
        </Field>
        <Field label="Status">
          <select name="status" defaultValue={value.status || "idea"}>
            {Object.entries(NICHE_STATUS).map(([id, item]) => <option key={id} value={id}>{item.label}</option>)}
          </select>
        </Field>
        <Field label="Conviction (0–100)" hint="Vận hành hypothesis, không phải xác suất thành công.">
          <input type="number" min="0" max="100" name="conviction" defaultValue={value.conviction ?? 50} />
        </Field>
        <label className="launch-asap-toggle span-2">
          <input
            type="checkbox"
            name="launch_asap"
            defaultChecked={Boolean(value.launch_asap)}
          />
          <span className="launch-asap-toggle-icon"><Flame size={17} /></span>
          <span>
            <strong>🔥 HOT</strong>
            <small>Đẩy niche lên đầu queue và làm card nổi bật.</small>
          </span>
        </label>
        <Field label="Market">
          <input name="market" defaultValue={value.market || "US"} />
        </Field>
        <Field label="Audience">
          <input name="audience" defaultValue={value.audience || ""} />
        </Field>
        <div className="multi-select-field span-2">
          <strong>Planned production formats</strong>
          <small>Có thể chọn nhiều format nếu niche sẽ chạy hybrid hoặc test nhiều pipeline.</small>
          <div className="multi-select-chips">
            {Object.entries(PRODUCTION_FORMATS)
              .filter(([id]) => id !== "hybrid")
              .map(([id, item]) => {
                const format = id as ProductionFormatId;
                const checked = plannedFormats.includes(format);
                return (
                  <label className={checked ? "selected" : ""} key={id}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) => setPlannedFormats((current) => {
                        if (event.target.checked) return [...new Set([...current, format])];
                        const next = current.filter((value) => value !== format);
                        return next.length ? next : current;
                      })}
                    />
                    <span>{item.label}</span>
                  </label>
                );
              })}
          </div>
        </div>
        <Field label="Reference channel 1">
          <input
            name="reference_channel"
            defaultValue={value.reference_channel || ""}
            placeholder="Channel name"
          />
        </Field>
        <Field label="Channel URL 1">
          <div className="url-field-row">
            <input
              type="url"
              name="reference_url"
              value={primaryReferenceUrl}
              onChange={(event) => setPrimaryReferenceUrl(event.target.value)}
              placeholder="https://youtube.com/@channel"
            />
            {safeExternalUrl(primaryReferenceUrl) ? (
              <a
                className="url-open-button"
                href={safeExternalUrl(primaryReferenceUrl)}
                target="_blank"
                rel="noreferrer"
                aria-label="Open reference URL 1"
                title="Open reference URL 1"
              >
                <ExternalLink size={15} />
              </a>
            ) : (
              <span className="url-open-button disabled" aria-hidden="true">
                <ExternalLink size={15} />
              </span>
            )}
          </div>
        </Field>
        {additionalReferences.map((reference, index) => (
          <div className="reference-pair span-2" key={index}>
            <label>
              <span>Reference channel {index + 2}</span>
              <input
                value={reference.name}
                onChange={(event) =>
                  setAdditionalReferences((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, name: event.target.value } : item
                    )
                  )
                }
                placeholder={`Reference channel ${index + 2}`}
              />
            </label>
            <label>
              <span>Channel URL {index + 2}</span>
              <div className="url-field-row">
                <input
                  type="url"
                  value={reference.url}
                  onChange={(event) =>
                    setAdditionalReferences((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, url: event.target.value } : item
                      )
                    )
                  }
                  placeholder="https://youtube.com/@channel"
                />
                {safeExternalUrl(reference.url) ? (
                  <a
                    className="url-open-button"
                    href={safeExternalUrl(reference.url)}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open reference URL ${index + 2}`}
                    title={`Open reference URL ${index + 2}`}
                  >
                    <ExternalLink size={15} />
                  </a>
                ) : (
                  <span className="url-open-button disabled" aria-hidden="true">
                    <ExternalLink size={15} />
                  </span>
                )}
              </div>
            </label>
            <button
              type="button"
              className="ghost-icon"
              onClick={() =>
                setAdditionalReferences((current) =>
                  current.filter((_, itemIndex) => itemIndex !== index)
                )
              }
              aria-label={`Remove reference ${index + 2}`}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="add-reference-button span-2"
          onClick={() =>
            setAdditionalReferences((current) => [
              ...current,
              { name: "", url: "" }
            ])
          }
        >
          <Plus size={15} />
          Add reference channel {additionalReferences.length + 2}
        </button>
        <Field label="Angle / thesis" span>
          <textarea name="angle" rows={4} defaultValue={value.angle || ""} />
        </Field>
        <Field label="Evidence & limits" span hint="Tách verified signal khỏi hypothesis và ghi ngày snapshot nếu có.">
          <textarea name="evidence" rows={6} defaultValue={value.evidence || ""} />
        </Field>
        <Field label="Product path" span>
          <input name="product_path" defaultValue={value.product_path || ""} placeholder="Lead magnet → ebook → bundle..." />
        </Field>
        <div className="obsidian-notes-editor span-2">
          <div className="obsidian-notes-heading">
            <strong>Obsidian research notes</strong>
            <small>Dán absolute path, path tương đối trong vault hoặc obsidian:// URI.</small>
          </div>
          {researchNotes.map((note, index) => (
            <div className="reference-pair obsidian-note-pair" key={index}>
              <label>
                <span>Note name {index + 1}</span>
                <input
                  value={note.name}
                  onChange={(event) =>
                    setResearchNotes((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, name: event.target.value } : item
                      )
                    )
                  }
                  placeholder={`Research note ${index + 1}`}
                />
              </label>
              <label>
                <span>Obsidian path / URI {index + 1}</span>
                <div className="url-field-row">
                  <input
                    value={note.note_path}
                    onChange={(event) =>
                      setResearchNotes((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, note_path: event.target.value } : item
                        )
                      )
                    }
                    placeholder="05-KNOWLEDGE/niche-collection/my-research-note.md"
                  />
                  {obsidianNoteUrl(note.note_path) ? (
                    <a
                      className="url-open-button obsidian"
                      href={obsidianNoteUrl(note.note_path)}
                      aria-label={`Open research note ${index + 1} in Obsidian`}
                      title={`Open research note ${index + 1} in Obsidian`}
                    >
                      <BookOpen size={15} />
                    </a>
                  ) : (
                    <span className="url-open-button obsidian disabled" aria-hidden="true">
                      <BookOpen size={15} />
                    </span>
                  )}
                </div>
              </label>
              <button
                type="button"
                className="ghost-icon"
                onClick={() =>
                  setResearchNotes((current) => {
                    const next = current.filter((_, itemIndex) => itemIndex !== index);
                    return next.length ? next : [{ name: "", note_path: "" }];
                  })
                }
                aria-label={`Remove research note ${index + 1}`}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="add-reference-button add-obsidian-note-button"
            onClick={() =>
              setResearchNotes((current) => [
                ...current,
                { name: "", note_path: "" }
              ])
            }
          >
            <Plus size={15} />
            Add research note {researchNotes.length + 1}
          </button>
        </div>
        <Field label="Notes / gates" span>
          <textarea name="notes" rows={5} defaultValue={value.notes || ""} />
        </Field>
      </form>
    </Modal>
  );
}

function IdeationPatternModal({
  value,
  onClose,
  onSave,
  onDelete
}: {
  value: Partial<IdeationPattern>;
  onClose: () => void;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
  onDelete?: () => void;
}) {
  const formId = "ideation-pattern-form";
  const [sourceUrl, setSourceUrl] = useState(value.source_url || "");
  return (
    <Modal
      title={value.id ? value.name || "Ideation pattern" : "Capture ideation pattern"}
      eyebrow="Ideation Pattern Library"
      onClose={onClose}
      wide
      footer={
        <ModalActions
          formId={formId}
          onClose={onClose}
          onDelete={onDelete}
          submitLabel={value.id ? "Save pattern" : "Add pattern"}
        />
      }
    >
      <form
        id={formId}
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(formValues(event.currentTarget));
        }}
      >
        <Field label="Pattern name" span>
          <input name="name" defaultValue={value.name || ""} required autoFocus />
        </Field>
        <Field label="Pattern family">
          <input
            name="family"
            defaultValue={value.family || ""}
            placeholder="Quantified Time Promise"
          />
        </Field>
        <Field label="Status">
          <select name="status" defaultValue={value.status || "captured"}>
            {Object.entries(PATTERN_STATUS).map(([id, item]) => (
              <option key={id} value={id}>{item.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Title formula" span>
          <input
            name="title_formula"
            defaultValue={value.title_formula || ""}
            placeholder="How to Fix [condition] — [problem / mechanism]"
          />
        </Field>
        <Field label="Thumbnail formula" span>
          <input
            name="thumbnail_formula"
            defaultValue={value.thumbnail_formula || ""}
            placeholder="ONE DAY FIX / [N] YEARS OF [PROBLEM]"
          />
        </Field>
        <Field label="Core hook" span>
          <textarea name="core_hook" rows={3} defaultValue={value.core_hook || ""} />
        </Field>
        <Field label="Target niche / application" span>
          <input name="target_niche" defaultValue={value.target_niche || ""} />
        </Field>
        <Field label="Source channel">
          <input name="source_channel" defaultValue={value.source_channel || ""} />
        </Field>
        <Field label="Source video URL">
          <div className="url-field-row">
            <input
              type="url"
              name="source_url"
              value={sourceUrl}
              onChange={(event) => setSourceUrl(event.target.value)}
              placeholder="https://youtube.com/watch?v=..."
            />
            {safeExternalUrl(sourceUrl) ? (
              <a
                className="url-open-button"
                href={safeExternalUrl(sourceUrl)}
                target="_blank"
                rel="noreferrer"
                aria-label="Open source video"
                title="Open source video"
              >
                <ExternalLink size={15} />
              </a>
            ) : (
              <span className="url-open-button disabled" aria-hidden="true">
                <ExternalLink size={15} />
              </span>
            )}
          </div>
        </Field>
        <Field label="Proof snapshot" span hint="Ghi views và ngày snapshot; không coi một outlier là guarantee.">
          <textarea name="proof_snapshot" rows={3} defaultValue={value.proof_snapshot || ""} />
        </Field>
        <Field label="Bending / generation prompt" span>
          <textarea name="bending_prompt" rows={5} defaultValue={value.bending_prompt || ""} />
        </Field>
        <Field label="Notes / test decision" span>
          <textarea name="notes" rows={4} defaultValue={value.notes || ""} />
        </Field>
      </form>
    </Modal>
  );
}

function ProductModal({
  value,
  channels,
  onClose,
  onSave,
  onDelete
}: {
  value: Partial<Product>;
  channels: Channel[];
  onClose: () => void;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
  onDelete?: () => void;
}) {
  const formId = "product-form";
  return (
    <Modal
      title={value.id ? value.name || "Product" : "Create monetization card"}
      eyebrow="Monetization Lab"
      onClose={onClose}
      wide
      footer={<ModalActions formId={formId} onClose={onClose} onDelete={onDelete} submitLabel={value.id ? "Save product" : "Add product"} />}
    >
      <form
        id={formId}
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          const payload = formValues(event.currentTarget);
          payload.price = payload.price === "" ? null : Number(payload.price);
          payload.units_sold = Number(payload.units_sold || 0);
          payload.revenue = Number(payload.revenue || 0);
          payload.channel_id = payload.channel_id || null;
          onSave(payload);
        }}
      >
        <Field label="Product name" span>
          <input name="name" defaultValue={value.name || ""} required autoFocus />
        </Field>
        <Field label="Connected channel">
          <select name="channel_id" defaultValue={value.channel_id || ""}>
            <option value="">Network / multiple channels</option>
            {channels.map((channel) => <option key={channel.id} value={channel.id}>{channel.name}</option>)}
          </select>
        </Field>
        <Field label="Stage">
          <select name="status" defaultValue={value.status || "idea"}>
            {PRODUCT_COLUMNS.map((column) => <option key={column.id} value={column.id}>{column.label}</option>)}
          </select>
        </Field>
        <Field label="Product type">
          <select name="type" defaultValue={value.type || "ebook"}>
            <option value="lead-magnet">Lead magnet</option>
            <option value="ebook">Ebook</option>
            <option value="printable">Printable</option>
            <option value="bundle">Bundle</option>
            <option value="course">Course</option>
            <option value="membership">Membership</option>
          </select>
        </Field>
        <Field label="Due date">
          <input type="date" name="due_date" defaultValue={value.due_date || ""} />
        </Field>
        <Field label="Price">
          <input type="number" step="0.01" name="price" defaultValue={value.price ?? ""} />
        </Field>
        <Field label="Currency">
          <input name="currency" defaultValue={value.currency || "USD"} />
        </Field>
        <Field label="Offer angle" span>
          <textarea name="offer_angle" rows={4} defaultValue={value.offer_angle || ""} />
        </Field>
        <Field label="Lead magnet / bridge" span>
          <textarea name="lead_magnet" rows={3} defaultValue={value.lead_magnet || ""} />
        </Field>
        <Field label="Sales page URL">
          <input type="url" name="sales_page_url" defaultValue={value.sales_page_url || ""} />
        </Field>
        <Field label="Checkout URL">
          <input type="url" name="checkout_url" defaultValue={value.checkout_url || ""} />
        </Field>
        <Field label="Units sold">
          <input type="number" min="0" name="units_sold" defaultValue={value.units_sold || 0} />
        </Field>
        <Field label="Tracked revenue">
          <input type="number" min="0" step="0.01" name="revenue" defaultValue={value.revenue || 0} />
        </Field>
        <Field label="Notes / next gate" span>
          <textarea name="notes" rows={6} defaultValue={value.notes || ""} />
        </Field>
      </form>
    </Modal>
  );
}

function EmptyCompact({ text }: { text: string }) {
  return <div className="empty-compact">{text}</div>;
}

export default App;
